import type { Lang } from "./api";

export const LANGS: Lang[] = ["pt", "en", "es", "fr", "de"];
export const LANG_NAMES: Record<Lang, string> = { pt: "Português", en: "English", es: "Español", fr: "Français", de: "Deutsch" };

export const isLang = (v: unknown): v is Lang => typeof v === "string" && (LANGS as string[]).includes(v);

const en = {
  appName: "My trips",
  loading: "Loading…",
  retry: "Try again",
  back: "Back",
  close: "Close",
  save: "Save",
  saving: "Saving…",
  saved: "Saved",
  cancel: "Cancel",
  comingSoon: "Coming soon",
  somethingWrong: "Something went wrong. Please try again.",
  offline: "You seem to be offline.",
  notFoundTitle: "This page does not exist",
  notFoundBody: "The address may be out of date. Your trips are one tap away.",
  goHome: "Go to my trips",

  // Header and navigation
  navHome: "Trips",
  navAccount: "Account",
  signOut: "Sign out",
  signIn: "Sign in",

  // Sign-in
  signInTitle: "My trips",
  signInLead: "Your trips, documents and consultant, all in one place.",
  stagingBanner: "Staging: emails, SMS and WhatsApp are simulated. Your sign-in code is in the outbox.",
  stagingOutbox: "Open the outbox",
  stagingTest: "Test system: nothing reaches a real inbox or phone. Your sign-in code is in the outbox.",
  continueWith: "Continue with {provider}",
  orDivider: "or",
  socialFailed: "Sign-in with {provider} did not finish. Try again, or use a code.",
  signInEmail: "Email",
  signInPhone: "Mobile number",
  usePhone: "Use my mobile number instead",
  useEmail: "Use my email instead",
  emailPlaceholder: "you@example.com",
  phonePlaceholder: "+351 9…",
  sendCode: "Send me a code",
  sending: "Sending…",
  codeSentTo: "We sent a code to {to}.",
  codeSentSms: "We sent a code by SMS to {to}.",
  enterCode: "Enter the code",
  codeHint: "Or open the link we sent you instead: it signs you in on its own.",
  resend: "Send a new code",
  resendIn: "Send a new code in {s} s",
  changeAddress: "Use another address",
  verifying: "Checking…",
  welcomeCreated: "Welcome! Your account is ready.",
  magicChecking: "Opening your link…",
  magicFailed: "This link no longer works. Ask for a new code below.",
  language: "Language",
  errInvalidAddress: "That does not look like a valid address. Please check it.",
  errTooManyCodes: "Too many codes requested. Please wait a few minutes and try again.",
  errUnknownPhone: "We do not know this number yet. Sign in with your email the first time; you can add the number afterwards.",
  errProviderUnavailable: "We cannot send codes right now. Please try again in a moment.",
  errWrongCode: "That code is not right. Please check and try again.",
  errChallengeExpired: "This code has expired. Ask for a new one.",
  errTooManyAttempts: "Too many attempts. Ask for a new code.",
  errNoBrand: "This address serves no brand. Open your brand's My trips.",

  // Home
  greeting: "Hello, {name}",
  greetingNoName: "Hello",
  nextTrip: "Your next trip",
  inDays: "In {n} days",
  tomorrow: "Tomorrow",
  today: "Today",
  ongoing: "Happening now",
  upcoming: "Also coming up",
  pastTrips: "Past trips",
  noTrips: "No trips here yet",
  noTripsBody: "When you book with us, your trip appears here with its timeline and documents.",
  proposalWaiting: "Your proposal is waiting",
  viewTrip: "View trip",
  travellers: "{n} travellers",
  traveller: "1 traveller",
  linkSessionNote: "You opened a link to one trip. Sign in with a code to see all your trips.",

  // Status
  statusProposal: "Proposal",
  statusPending: "Pending",
  statusConfirmed: "Confirmed",
  statusInProgress: "In progress",
  statusCompleted: "Completed",
  statusCancelled: "Cancelled",

  // Loyalty
  points: "points",
  tier: "Tier",
  joinClub: "Join {programme}",
  joinClubBody: "Earn points on every trip with any of our brands and enjoy benefits as you travel. Your consultant will enrol you with your first trip.",
  pointsExpire: "Points expire on {date}",

  // Consultant
  yourConsultant: "Your consultant",
  consultantAssigned: "A dedicated consultant looks after your trips. Their name and photo arrive here soon.",
  consultantNotYet: "Your consultant will be assigned with your first trip.",
  write: "Write",
  whatsapp: "WhatsApp",
  call: "Call",

  // Install
  installTitle: "Add My trips to your home screen",
  installBody: "One tap to your trips, documents and help, even on the move.",
  install: "Install",
  notNow: "Not now",

  // Trip
  reference: "Reference",
  timeline: "Timeline",
  timelineEmpty: "The day-by-day plan appears here once your trip is confirmed.",
  services: "Services",
  servicesEmpty: "No services listed yet.",
  travellersSection: "Travellers",
  holder: "Booking holder",
  companion: "Companion",
  inviteSoon: "Inviting companions is coming soon.",
  help: "Help",
  helpBody: "Your consultant answers behind the brand's channels, and our line is open 24 hours during your trip.",
  helpNow: "Help now",
  helpNowSoon: "Available during your trip",
  legacyNote: "From our previous system; documents on request.",
  tripDocuments: "Your documents",
  reviewTitle: "How was your stay?",
  reviewInvite: "Tell other travellers about {hotel} ({dates}). It takes two minutes.",
  reviewWrite: "Write a review",
  reviewOpenUntil: "Open until {date}",
  legacyImported: "Imported from our previous system ({ref}). Your consultant manages it here, like any other trip.",
  document: "Document",
  total: "Total",
  noDate: "Dates to be confirmed",
  // Timeline details
  flight: "Flight",
  pnr: "Booking code",
  terminal: "Terminal",
  checkIn: "Check-in",
  checkOut: "Check-out",
  room: "Room",
  board: "Board",
  rooms: "{n} rooms",
  meetingPoint: "Meeting point",
  vehicle: "Vehicle",
  flightNumber: "Flight",
  pickup: "Pick-up",
  dropoff: "Drop-off",
  duration: "Duration",
  supplier: "Supplier",
  departure: "Departure",
  arrival: "Arrival",

  // Account
  account: "Account",
  yourDetails: "Your details",
  name: "Name",
  email: "Email",
  phone: "Mobile number",
  consents: "Your choices",
  consentsBody: "We only write to you about your trips. Tick a brand to also receive its offers and inspiration.",
  marketingFrom: "News and offers from {brand}",
  messagesSent: "Messages we sent you",
  messagesEmpty: "Nothing yet.",
  messagesStagingNote: "This is a preview environment: messages are recorded here and not actually delivered.",
  openLink: "Open link",
  legal: "Legal",
  operatedBy: "Operated by {entity}",
  memberSince: "Customer since {date}",

  // Link opener
  openingLink: "Opening your link…",
  linkUnknown: "We do not recognise this link.",
  linkExpired: "This link has expired.",
  linkRevoked: "This link is no longer valid.",
  linkBody: "Sign in with a code to see your trips.",
  helpSoon: "Help now will be available here very soon.",

  // No brand
  chooseBrand: "Choose your brand",
  chooseBrandBody: "My trips is available on each of our brands. Open the one you booked with.",

  // Proposal
  proposalEyebrow: "Your proposal",
  preparedBy: "Prepared for you by {name}",
  validUntil: "Valid until {date}",
  aboutTrip: "About this trip",
  options: "Options",
  recommended: "Recommended",
  chosen: "Chosen",
  selectOption: "Select this option",
  dayByDay: "Your trip, day by day",
  dayN: "Day {n}",
  yourTotal: "Your total",
  earnsPoints: "Earns {points} {programme} points",
  depositLine: "Deposit {deposit} on acceptance · balance {balance} later",
  depositTerms: "Deposit on acceptance, balance later",
  questions: "Questions for your consultant",
  questionsEmpty: "Anything unclear? Ask here and your consultant answers on this page.",
  answerSoon: "Your consultant will answer soon",
  askPlaceholder: "Write your question…",
  ask: "Ask",
  acceptPay: "Accept and pay the deposit",
  preContractTitle: "Before you accept: pre-contract information",
  preContractLead: "This is a package holiday. The law asks us to give you this standard information before you pay: who organises your trip, your key rights and who protects you if we become insolvent.",
  preContractOpen: "Read the pre-contract information",
  preContractCheck: "I have read the pre-contract information and accept it.",
  preContractRequired: "Tick the box to confirm you have read the pre-contract information.",
  preContractChanged: "The proposal changed since you opened it. Read the updated information and tick again.",
  preContractAccepted: "Pre-contract information (version {version}), accepted on {date}",
  accepting: "One moment…",
  decline: "Not now / Decline",
  declineConfirm: "Decline this proposal? Your consultant will be told.",
  declineReason: "Tell us why (optional)",
  confirmDecline: "Yes, decline",
  proposalAccepted: "You accepted this proposal",
  proposalAcceptedBody: "Your trip is ready; the deposit is waiting on its page.",
  proposalDeclined: "This proposal was declined",
  proposalDeclinedBody: "If you change your mind, your consultant is a message away.",
  proposalExpired: "This proposal has expired",
  proposalExpiredBody: "Ask your consultant for an updated one.",
  proposalClosed: "This proposal is closed.",
  proposalNotYours: "This proposal belongs to another customer. Sign in with the address it was sent to.",
  proposalSignIn: "Sign in to open your proposal.",
  viewProposal: "View proposal",
  welcomeTrip: "Welcome to your trip",
  welcomeTripBody: "Pay the deposit below and we start booking everything.",

  // Payments
  payments: "Payments",
  deposit: "Deposit",
  balance: "Balance",
  instalmentN: "Instalment {n}",
  refund: "Refund",
  balanceDueOn: "Balance due on {date}",
  paidOf: "{paid} paid of {total}",
  outstanding: "Outstanding",
  dueOn: "Due {date}",
  payAmount: "Pay {amount}",
  payNow: "Pay now",
  paying: "Processing…",
  paymentMethod: "Payment method",
  methodCard: "Card",
  methodMbway: "MB Way",
  methodTransfer: "Bank transfer",
  methodApplePay: "Apple Pay",
  methodGooglePay: "Google Pay",
  simulation: "Simulation (preview environment)",
  simulationBody: "No money moves here. Choose how the payment should end.",
  simApprove: "Approve payment",
  simDecline: "Decline card",
  simPending: "Leave pending (bank transfer)",
  payPaid: "Payment received",
  payPaidBody: "Thank you. Your receipt is in your messages.",
  payFailed: "The payment did not go through",
  payFailedBody: "Please try again or choose another method.",
  payPending: "Payment pending",
  payPendingBody: "We confirm it as soon as the transfer arrives.",
  errAlreadyPaid: "This payment was already made.",
  errBadState: "This payment cannot be taken right now.",
  errInstalments: "Monthly payments are not possible for this balance.",
  errHolderOnly: "Only the booking holder can pay.",
  payMonthly: "Pay the balance monthly",
  payMonthlyBody: "Split the balance into monthly payments, all before the balance date.",
  monthsTimes: "{months} × {monthly}",
  firstLast: "First {first} · last {last}",
  instalmentsChosen: "Balance in {n} monthly payments",
  companionReadOnly: "The booking holder manages the payments.",
  noPlanYet: "Payment details arrive once the booking is set up.",
  statusDue: "Due",
  statusPaid: "Paid",
  statusFailed: "Failed",
  statusRefunded: "Refunded",
  planDepositDue: "Deposit due",
  planDepositPaid: "Deposit paid",
  planBalanceDue: "Balance due",
  planPaid: "Paid in full",
  planOverdue: "Overdue",
  planRefunded: "Refunded",

  // Travellers
  travellerDetails: "Traveller details",
  travellersDueBy: "Traveller details due by {date}",
  travellerN: "Traveller {n}",
  trMissing: "Missing",
  trSubmitted: "Submitted",
  trValidated: "Validated",
  trRejected: "Needs correction",
  enterDetails: "Enter details",
  edit: "Edit",
  givenNames: "Given names",
  surname: "Surname",
  dateOfBirth: "Date of birth",
  nationality: "Nationality",
  otherCountry: "Other country (2-letter code)",
  countryCode: "Country code",
  documentType: "Document",
  passport: "Passport",
  idCard: "ID card",
  documentNumber: "Document number",
  documentExpiry: "Expiry date",
  gender: "Gender (optional)",
  genderF: "Female",
  genderM: "Male",
  genderX: "Other / prefer not to say",
  docsNote: "Documents are never stored as images; only these fields.",
  errAlreadyValidated: "This traveller was validated; ask your consultant for a change.",
  rejectedReason: "Reason: {reason}",
  travellerSaved: "Details saved. Your consultant will check them.",

  // Requests
  requests: "Changes and cancellations",
  requestsBody: "Tell your consultant what you need; the reference goes with the message.",
  requestChange: "Request a change",
  requestCancellation: "Request a cancellation",
  changeSubject: "Change request · {ref}",
  cancelSubject: "Cancellation request · {ref}",

  // Home hints
  payDeposit: "Pay the deposit",
  enterTravellers: "Enter traveller details",
  balanceDueHint: "Balance due {date}",
  // C3: conversation, assistant, requests
  navMessages: "Messages",
  unread: "New messages",
  conversation: "Conversation",
  conversationLead: "Your messages with {name}, across email, WhatsApp and here.",
  conversationEmpty: "Write to {name}; the assistant answers right away, your consultant follows.",
  yourConsultantLower: "your consultant",
  assistant: "Assistant",
  channelHere: "Here",
  kindChangeRequest: "Change request",
  kindCancellationRequest: "Cancellation request",
  kindQuestion: "Question",
  kindHandoff: "Handed over to your consultant",
  writePlaceholder: "Write your message…",
  send: "Send",
  replyMeOn: "Reply me on",
  replyHere: "Here in the app",
  consultantTold: "Your consultant has been told.",
  assistantNote: "I answer instantly; your consultant takes over whenever you ask for a person.",
  assistantOpen: "Ask the assistant",
  assistantEmpty: "Ask about your documents, your plan for tomorrow or anything on your trip.",
  assistantPlaceholder: "Ask anything about your trip…",
  assistantThinking: "Thinking…",
  chipDocuments: "Where are my documents?",
  chipTomorrow: "What's the plan for tomorrow?",
  chipPerson: "Talk to a person",
  requestsNote: "Your consultant checks the terms with the suppliers and comes back with the exact cost before anything changes.",
  requestChangePlaceholder: "What would you like to change? Dates, travellers, hotel, flights…",
  requestCancelPlaceholder: "What would you like to cancel, and when from?",
  requestSent: "Request sent",
  requestSentBody: "Your consultant answers in your conversation.",
  openConversation: "Open the conversation",
  newRequest: "Make another request",
  errHolderOnlyRequest: "Only the booking holder can request changes or cancellations.",
  replyChannel: "Reply channel",
  replyChannelBody: "Where your consultant's replies reach you when you write from here.",
  writeToMe: "Write to me",
  speaks: "Speaks: {langs}",
  // C4: trip companion
  scheduleChanged: "Schedule changed",
  wasTimes: "was",
  statusDriverAssigned: "Driver assigned",
  statusEnRoute: "On the way",
  driver: "Driver",
  plate: "Plate",
  callDriver: "Call the driver",
  followLive: "Follow the car live",
  driverOnTheWay: "Your driver is on the way",
  nextUp: "Next",
  companions: "Companions",
  companionsBody: "Companions see the plan, the documents and get the notices; only you pay and speak for the booking.",
  companionsReadOnly: "The booking holder manages the companions.",
  invited: "Invited",
  joined: "Joined",
  inviteCompanion: "Invite a companion",
  nameOptional: "Name (optional)",
  invite: "Invite",
  inviting: "Inviting…",
  remove: "Remove",
  removeCompanionConfirm: "Remove {name} from this trip?",
  errAlreadyMember: "This person is already on the trip.",
  errTooManyCompanions: "This trip has reached its limit of companions.",
  errHolderOnlyCompanions: "Only the booking holder can invite companions.",
  tripNotices: "Trip notices",
  tripNoticesBody: "When we write to you about your trips, and where we try first.",
  noticesBefore: "Before the trip",
  noticesBeforeHint: "A summary a week before and tomorrow's plan the day before.",
  noticesDuring: "During the trip",
  noticesDuringHint: "Each morning's plan, flight changes and your driver.",
  noticesAfter: "After the trip",
  noticesAfterHint: "How it went and the points you earned.",
  channelOrder: "Where we reach you first",
  channelPush: "Notification on this device",
  moveUp: "Move up",
  moveDown: "Move down",
  thisDevice: "This device",
  enablePush: "Enable notifications on this device",
  enablingPush: "Enabling…",
  pushEnabled: "Notifications are on for this device.",
  pushDenied: "Notifications are blocked for this site in your browser settings.",
  pushUnsupported: "This browser cannot show notifications. Add the app to your home screen and try again from there.",
  pushSimulated: "Push is simulated on this environment: notices are visible in your messages.",
  pushFailed: "We could not enable notifications. Please try again.",
  noticesBannerTitle: "Get notices on your phone",
  noticesBannerBody: "Install the app and enable notifications: your plan each morning and live updates on flights and drivers.",
  noticesBannerAction: "Enable notifications",
  // C5: Getaways Club and Help now
  navClub: "Club",
  clubEmptyTitle: "Your {programme} account opens with your first trip",
  clubEmptyBody: "Every euro you spend with any of our three brands earns points, and 100 points take 1 euro off your next balance.",
  yourTier: "Your tier",
  progressToNext: "{pct}% to {tier}",
  topTier: "You are at the top tier",
  worth: "worth {value}",
  howItWorks: "How it works",
  howEarn: "1 point for every euro or dollar spent with any of our three brands.",
  howRedeem: "100 points = 1 unit off your balance payment.",
  howExpire: "Points expire 24 months after your last trip.",
  clubBrandsNote: "One account across Azores Getaways, Portugal Getaways and Yes Getaways.",
  tiersTitle: "Tiers",
  current: "Current",
  fromPoints: "From {points} points",
  fromFirstTrip: "From your first trip",
  viewClub: "See your benefits",
  usePoints: "Use your {programme} points",
  redeemBody: "You have {points} points worth {value} to use on this balance.",
  pointsToUse: "Points to use",
  applyPoints: "Apply points",
  applying: "Applying…",
  pointsApplied: "{points} points applied (−{value})",
  fewerPoints: "Fewer points",
  morePoints: "More points",
  errNoBalance: "You have no points to use on this balance yet.",
  errTooManyPoints: "That is more than you can use here. Choose a lower amount, in hundreds.",
  errNothingDue: "There is nothing left to pay on this trip.",
  errNotLinked: "Your loyalty account is not linked yet. Your consultant can help.",
  helpNowBody: "Tell us what is wrong. The assistant answers at once and our team is alerted.",
  helpWhat: "What is wrong?",
  helpWhatPlaceholder: "Describe what happened in a few words.",
  helpWhere: "Where are you? (optional)",
  helpWherePlaceholder: "Hotel, airport, street…",
  helpCanCall: "You can call me",
  helpPhone: "Phone number",
  helpPhoneHint: "We call you back on this number.",
  helpUrgency: "How urgent is it?",
  urgencyUrgent: "Urgent",
  urgencyUrgentHint: "I need help right now: missed flight, accident, no room.",
  urgencySoon: "Soon",
  urgencySoonHint: "Today, but I can wait a little.",
  urgencyInfo: "Just a question",
  urgencyInfoHint: "No rush, I need information.",
  agentCalled: "{agent} is being called now",
  lineAlerted: "Our 24h line has been alerted.",
  call24h: "Call our 24h line",
  viewInMessages: "View in Messages",
  previousCases: "Previous cases",
  openedAgo: "Opened {when}",
  statusOpen: "Open",
  statusClosed: "Closed",
  helpSent: "We have your request",
  // C6: launch (privacy notice, consents, data requests)
  privacy: "Privacy",
  privacyVersion: "Version {version} · Controller: {controller}",
  privacyBody: "Your data and your choices. Ideastation is the controller for the three brands; one account, one privacy notice.",
  readPrivacy: "Read the privacy notice",
  consentTitle: "One family of brands, one account",
  consentController: "Ideastation, Lda. is the controller of your data for the three brands.",
  iUnderstand: "I understand",
  consentMarketingTitle: "Also hear from us (optional)",
  serviceAgreement: "Service agreement",
  consentAccepted: "Accepted {brand}",
  consentPending: "Not yet accepted",
  yourData: "Your data",
  exportData: "Export my data",
  deleteAccount: "Delete my account",
  dataRequestsNote: "The export is a JSON file with everything this area holds about you. Deleting your account erases your personal data; your trips stay in our records as business documents, without your name.",
  exporting: "Preparing your file…",
  exported: "File saved",
  deleteAccountExplain: "Your trips stay in our records as business documents, without your name. We email you a confirmation link, valid for 48 hours; your account is deleted only when you open it.",
  deleteAccountConfirm: "Send me the confirmation link",
  deleteRequested: "We sent you a confirmation email. Open the link in it within 48 hours to delete your account.",
  deleteConfirmTitle: "Delete your account",
  deleteConfirmBody: "This erases your account and your personal data for good. Your trips stay in our records as business documents, without your name.",
  deleteForGood: "Delete for good",
  deleting: "Deleting…",
  keepAccount: "Keep my account",
  deleteDone: "Your account was deleted.",
  deleteDoneBody: "Thank you for travelling with us. You can close this page.",
  deleteLinkExpired: "This link has expired or was already used.",
};

export type Catalog = typeof en;
export type Key = keyof Catalog;

const pt: Catalog = {
  appName: "As minhas viagens",
  loading: "A carregar…",
  retry: "Tentar de novo",
  back: "Voltar",
  close: "Fechar",
  save: "Guardar",
  saving: "A guardar…",
  saved: "Guardado",
  cancel: "Cancelar",
  comingSoon: "Em breve",
  somethingWrong: "Algo correu mal. Tente de novo, por favor.",
  offline: "Parece que está sem ligação.",
  notFoundTitle: "Esta página não existe",
  notFoundBody: "O endereço pode estar desatualizado. As suas viagens estão a um toque.",
  goHome: "Ir para as minhas viagens",
  navHome: "Viagens",
  navAccount: "Conta",
  signOut: "Terminar sessão",
  signIn: "Entrar",
  signInTitle: "As minhas viagens",
  signInLead: "As suas viagens, documentos e consultor, tudo num só lugar.",
  stagingBanner: "Staging: emails, SMS e WhatsApp são simulados. O seu código de acesso está na caixa de saída.",
  stagingOutbox: "Abrir a caixa de saída",
  stagingTest: "Sistema de teste: nada chega a uma caixa de correio ou telefone reais. O seu código de acesso está na caixa de saída.",
  continueWith: "Continuar com {provider}",
  orDivider: "ou",
  socialFailed: "O acesso com {provider} não terminou. Tente outra vez ou use um código.",
  signInEmail: "Email",
  signInPhone: "Telemóvel",
  usePhone: "Usar o meu telemóvel",
  useEmail: "Usar o meu email",
  emailPlaceholder: "voce@exemplo.com",
  phonePlaceholder: "+351 9…",
  sendCode: "Enviar-me um código",
  sending: "A enviar…",
  codeSentTo: "Enviámos um código para {to}.",
  codeSentSms: "Enviámos um código por SMS para {to}.",
  enterCode: "Introduza o código",
  codeHint: "Ou abra o link que lhe enviámos: entra sozinho.",
  resend: "Enviar novo código",
  resendIn: "Enviar novo código em {s} s",
  changeAddress: "Usar outro endereço",
  verifying: "A verificar…",
  welcomeCreated: "Bem-vindo! A sua conta está pronta.",
  magicChecking: "A abrir o seu link…",
  magicFailed: "Este link já não funciona. Peça um novo código abaixo.",
  language: "Idioma",
  errInvalidAddress: "Esse endereço não parece válido. Verifique, por favor.",
  errTooManyCodes: "Pediu demasiados códigos. Aguarde uns minutos e tente de novo.",
  errUnknownPhone: "Ainda não conhecemos este número. Entre com o seu email da primeira vez; depois pode juntar o número.",
  errProviderUnavailable: "Não conseguimos enviar códigos neste momento. Tente de novo daqui a pouco.",
  errWrongCode: "Esse código não está certo. Verifique e tente de novo.",
  errChallengeExpired: "Este código expirou. Peça um novo.",
  errTooManyAttempts: "Demasiadas tentativas. Peça um novo código.",
  errNoBrand: "Este endereço não serve nenhuma marca. Abra o As minhas viagens da sua marca.",
  greeting: "Olá, {name}",
  greetingNoName: "Olá",
  nextTrip: "A sua próxima viagem",
  inDays: "Faltam {n} dias",
  tomorrow: "Amanhã",
  today: "Hoje",
  ongoing: "A decorrer",
  upcoming: "Também a caminho",
  pastTrips: "Viagens anteriores",
  noTrips: "Ainda não há viagens aqui",
  noTripsBody: "Quando reservar connosco, a sua viagem aparece aqui com o roteiro e os documentos.",
  proposalWaiting: "A sua proposta está à espera",
  viewTrip: "Ver viagem",
  travellers: "{n} viajantes",
  traveller: "1 viajante",
  linkSessionNote: "Abriu um link para uma viagem. Entre com um código para ver todas as suas viagens.",
  statusProposal: "Proposta",
  statusPending: "Pendente",
  statusConfirmed: "Confirmada",
  statusInProgress: "A decorrer",
  statusCompleted: "Concluída",
  statusCancelled: "Cancelada",
  points: "pontos",
  tier: "Nível",
  joinClub: "Junte-se ao {programme}",
  joinClubBody: "Ganhe pontos em cada viagem com qualquer uma das nossas marcas e desfrute de vantagens enquanto viaja. O seu consultor inscreve-o com a primeira viagem.",
  pointsExpire: "Os pontos expiram a {date}",
  yourConsultant: "O seu consultor",
  consultantAssigned: "Um consultor dedicado cuida das suas viagens. O nome e a fotografia chegam aqui em breve.",
  consultantNotYet: "O seu consultor será atribuído com a sua primeira viagem.",
  write: "Escrever",
  whatsapp: "WhatsApp",
  call: "Ligar",
  installTitle: "Adicione As minhas viagens ao ecrã inicial",
  installBody: "Um toque para as suas viagens, documentos e ajuda, mesmo em movimento.",
  install: "Instalar",
  notNow: "Agora não",
  reference: "Referência",
  timeline: "Roteiro",
  timelineEmpty: "O plano dia a dia aparece aqui assim que a viagem estiver confirmada.",
  services: "Serviços",
  servicesEmpty: "Ainda sem serviços listados.",
  travellersSection: "Viajantes",
  holder: "Titular da reserva",
  companion: "Acompanhante",
  inviteSoon: "Convidar acompanhantes está para breve.",
  help: "Ajuda",
  helpBody: "O seu consultor responde nos canais da marca, e a nossa linha está aberta 24 horas durante a viagem.",
  helpNow: "Ajuda agora",
  helpNowSoon: "Disponível durante a viagem",
  legacyNote: "Do nosso sistema anterior; documentos a pedido.",
  tripDocuments: "Os seus documentos",
  reviewTitle: "Como correu a estadia?",
  reviewInvite: "Conte a outros viajantes como foi o {hotel} ({dates}). Demora dois minutos.",
  reviewWrite: "Escrever opinião",
  reviewOpenUntil: "Aberto até {date}",
  legacyImported: "Importada do nosso sistema anterior ({ref}). O seu consultor gere-a aqui, como qualquer outra viagem.",
  document: "Documento",
  total: "Total",
  noDate: "Datas a confirmar",
  flight: "Voo",
  pnr: "Código de reserva",
  terminal: "Terminal",
  checkIn: "Check-in",
  checkOut: "Check-out",
  room: "Quarto",
  board: "Regime",
  rooms: "{n} quartos",
  meetingPoint: "Ponto de encontro",
  vehicle: "Veículo",
  flightNumber: "Voo",
  pickup: "Recolha",
  dropoff: "Entrega",
  duration: "Duração",
  supplier: "Fornecedor",
  departure: "Partida",
  arrival: "Chegada",
  account: "Conta",
  yourDetails: "Os seus dados",
  name: "Nome",
  email: "Email",
  phone: "Telemóvel",
  consents: "As suas escolhas",
  consentsBody: "Só lhe escrevemos sobre as suas viagens. Assinale uma marca para receber também as suas ofertas e inspiração.",
  marketingFrom: "Novidades e ofertas da {brand}",
  messagesSent: "Mensagens que lhe enviámos",
  messagesEmpty: "Ainda nada.",
  messagesStagingNote: "Este é um ambiente de pré-visualização: as mensagens ficam registadas aqui e não são realmente entregues.",
  openLink: "Abrir link",
  legal: "Legal",
  operatedBy: "Operado por {entity}",
  memberSince: "Cliente desde {date}",
  openingLink: "A abrir o seu link…",
  linkUnknown: "Não reconhecemos este link.",
  linkExpired: "Este link expirou.",
  linkRevoked: "Este link já não é válido.",
  linkBody: "Entre com um código para ver as suas viagens.",
  helpSoon: "A ajuda imediata vai estar disponível aqui muito em breve.",
  chooseBrand: "Escolha a sua marca",
  chooseBrandBody: "As minhas viagens está disponível em cada uma das nossas marcas. Abra aquela com que reservou.",

  proposalEyebrow: "A sua proposta",
  preparedBy: "Preparada para si por {name}",
  validUntil: "Válida até {date}",
  aboutTrip: "Sobre esta viagem",
  options: "Opções",
  recommended: "Recomendada",
  chosen: "Escolhida",
  selectOption: "Escolher esta opção",
  dayByDay: "A sua viagem, dia a dia",
  dayN: "Dia {n}",
  yourTotal: "O seu total",
  earnsPoints: "Ganha {points} pontos {programme}",
  depositLine: "Sinal de {deposit} na aceitação · restante {balance} mais tarde",
  depositTerms: "Sinal na aceitação, restante mais tarde",
  questions: "Perguntas ao seu consultor",
  questionsEmpty: "Alguma dúvida? Pergunte aqui e o seu consultor responde nesta página.",
  answerSoon: "O seu consultor responde em breve",
  askPlaceholder: "Escreva a sua pergunta…",
  ask: "Perguntar",
  acceptPay: "Aceitar e pagar o sinal",
  preContractTitle: "Antes de aceitar: informação pré-contratual",
  preContractLead: "Esta é uma viagem organizada. A lei pede-nos que lhe entreguemos esta informação normalizada antes do pagamento: quem organiza a sua viagem, os seus direitos principais e quem o protege em caso de insolvência.",
  preContractOpen: "Ler a informação pré-contratual",
  preContractCheck: "Li a informação pré-contratual e aceito-a.",
  preContractRequired: "Assinale a caixa para confirmar que leu a informação pré-contratual.",
  preContractChanged: "A proposta mudou desde que a abriu. Leia a informação atualizada e assinale de novo.",
  preContractAccepted: "Informação pré-contratual (versão {version}), aceite em {date}",
  accepting: "Um momento…",
  decline: "Agora não / Recusar",
  declineConfirm: "Recusar esta proposta? O seu consultor será informado.",
  declineReason: "Diga-nos porquê (opcional)",
  confirmDecline: "Sim, recusar",
  proposalAccepted: "Aceitou esta proposta",
  proposalAcceptedBody: "A sua viagem está pronta; o sinal espera na página dela.",
  proposalDeclined: "Esta proposta foi recusada",
  proposalDeclinedBody: "Se mudar de ideias, o seu consultor está à distância de uma mensagem.",
  proposalExpired: "Esta proposta expirou",
  proposalExpiredBody: "Peça ao seu consultor uma proposta atualizada.",
  proposalClosed: "Esta proposta está fechada.",
  proposalNotYours: "Esta proposta pertence a outro cliente. Entre com o endereço para onde foi enviada.",
  proposalSignIn: "Entre para abrir a sua proposta.",
  viewProposal: "Ver proposta",
  welcomeTrip: "Bem-vindo à sua viagem",
  welcomeTripBody: "Pague o sinal abaixo e começamos a reservar tudo.",
  payments: "Pagamentos",
  deposit: "Sinal",
  balance: "Restante",
  instalmentN: "Prestação {n}",
  refund: "Reembolso",
  balanceDueOn: "Restante a pagar até {date}",
  paidOf: "{paid} pagos de {total}",
  outstanding: "Em falta",
  dueOn: "Vence a {date}",
  payAmount: "Pagar {amount}",
  payNow: "Pagar agora",
  paying: "A processar…",
  paymentMethod: "Método de pagamento",
  methodCard: "Cartão",
  methodMbway: "MB Way",
  methodTransfer: "Transferência bancária",
  methodApplePay: "Apple Pay",
  methodGooglePay: "Google Pay",
  simulation: "Simulação (ambiente de pré-visualização)",
  simulationBody: "Aqui não há dinheiro a circular. Escolha como o pagamento deve terminar.",
  simApprove: "Aprovar pagamento",
  simDecline: "Recusar cartão",
  simPending: "Deixar pendente (transferência)",
  payPaid: "Pagamento recebido",
  payPaidBody: "Obrigado. O recibo está nas suas mensagens.",
  payFailed: "O pagamento não foi aceite",
  payFailedBody: "Tente de novo ou escolha outro método.",
  payPending: "Pagamento pendente",
  payPendingBody: "Confirmamos assim que a transferência chegar.",
  errAlreadyPaid: "Este pagamento já foi feito.",
  errBadState: "Este pagamento não pode ser feito neste momento.",
  errInstalments: "Não é possível pagar este restante em prestações.",
  errHolderOnly: "Só o titular da reserva pode pagar.",
  payMonthly: "Pagar o restante mensalmente",
  payMonthlyBody: "Divida o restante em prestações mensais, todas antes da data limite.",
  monthsTimes: "{months} × {monthly}",
  firstLast: "Primeira {first} · última {last}",
  instalmentsChosen: "Restante em {n} prestações mensais",
  companionReadOnly: "O titular da reserva gere os pagamentos.",
  noPlanYet: "Os detalhes de pagamento chegam assim que a reserva estiver montada.",
  statusDue: "A pagar",
  statusPaid: "Pago",
  statusFailed: "Falhou",
  statusRefunded: "Reembolsado",
  planDepositDue: "Sinal a pagar",
  planDepositPaid: "Sinal pago",
  planBalanceDue: "Restante a pagar",
  planPaid: "Totalmente pago",
  planOverdue: "Em atraso",
  planRefunded: "Reembolsado",
  travellerDetails: "Dados dos viajantes",
  travellersDueBy: "Dados dos viajantes até {date}",
  travellerN: "Viajante {n}",
  trMissing: "Em falta",
  trSubmitted: "Enviados",
  trValidated: "Validados",
  trRejected: "A corrigir",
  enterDetails: "Preencher dados",
  edit: "Editar",
  givenNames: "Nomes próprios",
  surname: "Apelido",
  dateOfBirth: "Data de nascimento",
  nationality: "Nacionalidade",
  otherCountry: "Outro país (código de 2 letras)",
  countryCode: "Código do país",
  documentType: "Documento",
  passport: "Passaporte",
  idCard: "Cartão de cidadão",
  documentNumber: "Número do documento",
  documentExpiry: "Data de validade",
  gender: "Género (opcional)",
  genderF: "Feminino",
  genderM: "Masculino",
  genderX: "Outro / prefiro não dizer",
  docsNote: "Os documentos nunca são guardados como imagem; apenas estes campos.",
  errAlreadyValidated: "Este viajante já foi validado; peça a alteração ao seu consultor.",
  rejectedReason: "Motivo: {reason}",
  travellerSaved: "Dados guardados. O seu consultor vai verificá-los.",
  requests: "Alterações e cancelamentos",
  requestsBody: "Diga ao seu consultor o que precisa; a referência segue com a mensagem.",
  requestChange: "Pedir uma alteração",
  requestCancellation: "Pedir um cancelamento",
  changeSubject: "Pedido de alteração · {ref}",
  cancelSubject: "Pedido de cancelamento · {ref}",
  payDeposit: "Pagar o sinal",
  enterTravellers: "Preencher dados dos viajantes",
  balanceDueHint: "Restante a pagar até {date}",
  // C3: conversation, assistant, requests
  navMessages: "Mensagens",
  unread: "Novas mensagens",
  conversation: "Conversa",
  conversationLead: "As suas mensagens com {name}, por email, WhatsApp e aqui.",
  conversationEmpty: "Escreva para {name}; o assistente responde de imediato e o seu consultor dá seguimento.",
  yourConsultantLower: "o seu consultor",
  assistant: "Assistente",
  channelHere: "Aqui",
  kindChangeRequest: "Pedido de alteração",
  kindCancellationRequest: "Pedido de cancelamento",
  kindQuestion: "Pergunta",
  kindHandoff: "Passado ao seu consultor",
  writePlaceholder: "Escreva a sua mensagem…",
  send: "Enviar",
  replyMeOn: "Responder-me por",
  replyHere: "Aqui na app",
  consultantTold: "O seu consultor foi avisado.",
  assistantNote: "Respondo de imediato; o seu consultor assume sempre que pedir uma pessoa.",
  assistantOpen: "Perguntar ao assistente",
  assistantEmpty: "Pergunte pelos seus documentos, pelo plano de amanhã ou por qualquer detalhe da viagem.",
  assistantPlaceholder: "Pergunte o que quiser sobre a viagem…",
  assistantThinking: "A pensar…",
  chipDocuments: "Onde estão os meus documentos?",
  chipTomorrow: "Qual é o plano para amanhã?",
  chipPerson: "Falar com uma pessoa",
  requestsNote: "O seu consultor confirma as condições com os fornecedores e volta com o custo exato antes de qualquer alteração.",
  requestChangePlaceholder: "O que gostaria de alterar? Datas, viajantes, hotel, voos…",
  requestCancelPlaceholder: "O que gostaria de cancelar, e a partir de quando?",
  requestSent: "Pedido enviado",
  requestSentBody: "O seu consultor responde na sua conversa.",
  openConversation: "Abrir a conversa",
  newRequest: "Fazer outro pedido",
  errHolderOnlyRequest: "Só o titular da reserva pode pedir alterações ou cancelamentos.",
  replyChannel: "Canal de resposta",
  replyChannelBody: "Onde as respostas do seu consultor chegam quando escreve a partir daqui.",
  writeToMe: "Escrever-me",
  speaks: "Fala: {langs}",
  // C4: trip companion
  scheduleChanged: "Horário alterado",
  wasTimes: "era",
  statusDriverAssigned: "Motorista atribuído",
  statusEnRoute: "A caminho",
  driver: "Motorista",
  plate: "Matrícula",
  callDriver: "Ligar ao motorista",
  followLive: "Seguir o carro em direto",
  driverOnTheWay: "O seu motorista está a caminho",
  nextUp: "A seguir",
  companions: "Acompanhantes",
  companionsBody: "Os acompanhantes veem o plano, os documentos e recebem os avisos; só você paga e fala pela reserva.",
  companionsReadOnly: "O titular da reserva gere os acompanhantes.",
  invited: "Convidado",
  joined: "Entrou",
  inviteCompanion: "Convidar um acompanhante",
  nameOptional: "Nome (opcional)",
  invite: "Convidar",
  inviting: "A convidar…",
  remove: "Remover",
  removeCompanionConfirm: "Remover {name} desta viagem?",
  errAlreadyMember: "Esta pessoa já faz parte da viagem.",
  errTooManyCompanions: "Esta viagem atingiu o limite de acompanhantes.",
  errHolderOnlyCompanions: "Só o titular da reserva pode convidar acompanhantes.",
  tripNotices: "Avisos da viagem",
  tripNoticesBody: "Quando lhe escrevemos sobre as suas viagens e por onde tentamos primeiro.",
  noticesBefore: "Antes da viagem",
  noticesBeforeHint: "Um resumo uma semana antes e o plano de amanhã na véspera.",
  noticesDuring: "Durante a viagem",
  noticesDuringHint: "O plano de cada manhã, alterações de voos e o seu motorista.",
  noticesAfter: "Depois da viagem",
  noticesAfterHint: "Como correu e os pontos que ganhou.",
  channelOrder: "Por onde o contactamos primeiro",
  channelPush: "Notificação neste dispositivo",
  moveUp: "Subir",
  moveDown: "Descer",
  thisDevice: "Este dispositivo",
  enablePush: "Ativar notificações neste dispositivo",
  enablingPush: "A ativar…",
  pushEnabled: "As notificações estão ativas neste dispositivo.",
  pushDenied: "As notificações estão bloqueadas para este site nas definições do navegador.",
  pushUnsupported: "Este navegador não mostra notificações. Adicione a app ao ecrã inicial e tente a partir daí.",
  pushSimulated: "O push é simulado neste ambiente: os avisos ficam visíveis nas suas mensagens.",
  pushFailed: "Não foi possível ativar as notificações. Tente de novo.",
  noticesBannerTitle: "Receba os avisos no telemóvel",
  noticesBannerBody: "Instale a app e ative as notificações: o plano de cada manhã e novidades em direto sobre voos e motoristas.",
  noticesBannerAction: "Ativar notificações",
  // C5: Getaways Club e Ajuda agora
  navClub: "Club",
  clubEmptyTitle: "A sua conta {programme} abre com a primeira viagem",
  clubEmptyBody: "Cada euro gasto com qualquer uma das nossas três marcas vale pontos, e 100 pontos descontam 1 euro no próximo restante.",
  yourTier: "O seu nível",
  progressToNext: "{pct}% para {tier}",
  topTier: "Está no nível mais alto",
  worth: "vale {value}",
  howItWorks: "Como funciona",
  howEarn: "1 ponto por cada euro ou dólar gasto com qualquer uma das nossas três marcas.",
  howRedeem: "100 pontos = 1 unidade de desconto no pagamento do restante.",
  howExpire: "Os pontos expiram 24 meses após a sua última viagem.",
  clubBrandsNote: "Uma só conta na Azores Getaways, Portugal Getaways e Yes Getaways.",
  tiersTitle: "Níveis",
  current: "Atual",
  fromPoints: "A partir de {points} pontos",
  fromFirstTrip: "Desde a primeira viagem",
  viewClub: "Ver as suas vantagens",
  usePoints: "Use os seus pontos {programme}",
  redeemBody: "Tem {points} pontos, no valor de {value}, para usar neste restante.",
  pointsToUse: "Pontos a usar",
  applyPoints: "Aplicar pontos",
  applying: "A aplicar…",
  pointsApplied: "{points} pontos aplicados (−{value})",
  fewerPoints: "Menos pontos",
  morePoints: "Mais pontos",
  errNoBalance: "Ainda não tem pontos para usar neste restante.",
  errTooManyPoints: "É mais do que pode usar aqui. Escolha um valor menor, em centenas.",
  errNothingDue: "Não há nada por pagar nesta viagem.",
  errNotLinked: "A sua conta de fidelização ainda não está ligada. O seu consultor pode ajudar.",
  helpNowBody: "Diga-nos o que se passa. O assistente responde de imediato e a nossa equipa é alertada.",
  helpWhat: "O que se passa?",
  helpWhatPlaceholder: "Descreva o que aconteceu em poucas palavras.",
  helpWhere: "Onde está? (opcional)",
  helpWherePlaceholder: "Hotel, aeroporto, rua…",
  helpCanCall: "Podem ligar-me",
  helpPhone: "Número de telefone",
  helpPhoneHint: "Ligamos-lhe de volta para este número.",
  helpUrgency: "Qual a urgência?",
  urgencyUrgent: "Urgente",
  urgencyUrgentHint: "Preciso de ajuda já: voo perdido, acidente, sem quarto.",
  urgencySoon: "Em breve",
  urgencySoonHint: "Hoje, mas posso esperar um pouco.",
  urgencyInfo: "Só uma pergunta",
  urgencyInfoHint: "Sem pressa, preciso de informação.",
  agentCalled: "{agent} está a ser contactado agora",
  lineAlerted: "A nossa linha 24h foi alertada.",
  call24h: "Ligar para a linha 24h",
  viewInMessages: "Ver nas Mensagens",
  previousCases: "Casos anteriores",
  openedAgo: "Aberto {when}",
  statusOpen: "Aberto",
  statusClosed: "Fechado",
  helpSent: "Recebemos o seu pedido",
  // C6: launch (privacy notice, consents, data requests)
  privacy: "Privacidade",
  privacyVersion: "Versão {version} · Responsável: {controller}",
  privacyBody: "Os seus dados e as suas escolhas. A Ideastation é a responsável pelo tratamento nas três marcas; uma conta, uma política de privacidade.",
  readPrivacy: "Ler a política de privacidade",
  consentTitle: "Uma família de marcas, uma conta",
  consentController: "A Ideastation, Lda. é a responsável pelo tratamento dos seus dados nas três marcas.",
  iUnderstand: "Compreendi",
  consentMarketingTitle: "Receber também novidades (opcional)",
  serviceAgreement: "Acordo de serviço",
  consentAccepted: "Aceite {brand}",
  consentPending: "Ainda não aceite",
  yourData: "Os seus dados",
  exportData: "Exportar os meus dados",
  deleteAccount: "Apagar a minha conta",
  dataRequestsNote: "A exportação é um ficheiro JSON com tudo o que esta área guarda sobre si. Apagar a conta elimina os seus dados pessoais; as viagens ficam nos nossos registos como documentos comerciais, sem o seu nome.",
  exporting: "A preparar o ficheiro…",
  exported: "Ficheiro guardado",
  deleteAccountExplain: "As suas viagens ficam nos nossos registos como documentos comerciais, sem o seu nome. Enviamos-lhe por email um link de confirmação, válido 48 horas; a conta só é apagada quando o abrir.",
  deleteAccountConfirm: "Enviar o link de confirmação",
  deleteRequested: "Enviámos-lhe um email de confirmação. Abra o link nas próximas 48 horas para apagar a conta.",
  deleteConfirmTitle: "Apagar a sua conta",
  deleteConfirmBody: "Isto apaga definitivamente a sua conta e os seus dados pessoais. As viagens ficam nos nossos registos como documentos comerciais, sem o seu nome.",
  deleteForGood: "Apagar definitivamente",
  deleting: "A apagar…",
  keepAccount: "Manter a minha conta",
  deleteDone: "A sua conta foi apagada.",
  deleteDoneBody: "Obrigado por viajar connosco. Pode fechar esta página.",
  deleteLinkExpired: "Este link expirou ou já foi utilizado.",
};

const es: Catalog = {
  appName: "Mis viajes",
  loading: "Cargando…",
  retry: "Reintentar",
  back: "Volver",
  close: "Cerrar",
  save: "Guardar",
  saving: "Guardando…",
  saved: "Guardado",
  cancel: "Cancelar",
  comingSoon: "Próximamente",
  somethingWrong: "Algo ha fallado. Inténtelo de nuevo, por favor.",
  offline: "Parece que no tiene conexión.",
  notFoundTitle: "Esta página no existe",
  notFoundBody: "La dirección puede estar desactualizada. Sus viajes están a un toque.",
  goHome: "Ir a mis viajes",
  navHome: "Viajes",
  navAccount: "Cuenta",
  signOut: "Cerrar sesión",
  signIn: "Entrar",
  signInTitle: "Mis viajes",
  signInLead: "Sus viajes, documentos y consultor, todo en un solo lugar.",
  stagingBanner: "Staging: los emails, SMS y WhatsApp son simulados. Su código de acceso está en la bandeja de salida.",
  stagingOutbox: "Abrir la bandeja de salida",
  stagingTest: "Sistema de prueba: nada llega a un buzón o teléfono reales. Su código de acceso está en la bandeja de salida.",
  continueWith: "Continuar con {provider}",
  orDivider: "o",
  socialFailed: "El acceso con {provider} no terminó. Inténtelo de nuevo o use un código.",
  signInEmail: "Email",
  signInPhone: "Móvil",
  usePhone: "Usar mi móvil",
  useEmail: "Usar mi email",
  emailPlaceholder: "usted@ejemplo.com",
  phonePlaceholder: "+34 6…",
  sendCode: "Enviarme un código",
  sending: "Enviando…",
  codeSentTo: "Hemos enviado un código a {to}.",
  codeSentSms: "Hemos enviado un código por SMS a {to}.",
  enterCode: "Introduzca el código",
  codeHint: "O abra el enlace que le hemos enviado: inicia sesión por sí solo.",
  resend: "Enviar un código nuevo",
  resendIn: "Enviar un código nuevo en {s} s",
  changeAddress: "Usar otra dirección",
  verifying: "Comprobando…",
  welcomeCreated: "¡Bienvenido! Su cuenta está lista.",
  magicChecking: "Abriendo su enlace…",
  magicFailed: "Este enlace ya no funciona. Pida un código nuevo abajo.",
  language: "Idioma",
  errInvalidAddress: "Esa dirección no parece válida. Compruébela, por favor.",
  errTooManyCodes: "Ha pedido demasiados códigos. Espere unos minutos e inténtelo de nuevo.",
  errUnknownPhone: "Aún no conocemos este número. Entre con su email la primera vez; después podrá añadir el número.",
  errProviderUnavailable: "No podemos enviar códigos ahora mismo. Inténtelo de nuevo en un momento.",
  errWrongCode: "Ese código no es correcto. Compruébelo e inténtelo de nuevo.",
  errChallengeExpired: "Este código ha caducado. Pida uno nuevo.",
  errTooManyAttempts: "Demasiados intentos. Pida un código nuevo.",
  errNoBrand: "Esta dirección no sirve ninguna marca. Abra Mis viajes de su marca.",
  greeting: "Hola, {name}",
  greetingNoName: "Hola",
  nextTrip: "Su próximo viaje",
  inDays: "Faltan {n} días",
  tomorrow: "Mañana",
  today: "Hoy",
  ongoing: "En curso",
  upcoming: "También en camino",
  pastTrips: "Viajes anteriores",
  noTrips: "Aún no hay viajes aquí",
  noTripsBody: "Cuando reserve con nosotros, su viaje aparecerá aquí con su itinerario y documentos.",
  proposalWaiting: "Su propuesta le espera",
  viewTrip: "Ver viaje",
  travellers: "{n} viajeros",
  traveller: "1 viajero",
  linkSessionNote: "Ha abierto un enlace a un viaje. Entre con un código para ver todos sus viajes.",
  statusProposal: "Propuesta",
  statusPending: "Pendiente",
  statusConfirmed: "Confirmado",
  statusInProgress: "En curso",
  statusCompleted: "Completado",
  statusCancelled: "Cancelado",
  points: "puntos",
  tier: "Nivel",
  joinClub: "Únase a {programme}",
  joinClubBody: "Gane puntos en cada viaje con cualquiera de nuestras marcas y disfrute de ventajas mientras viaja. Su consultor le inscribirá con su primer viaje.",
  pointsExpire: "Los puntos caducan el {date}",
  yourConsultant: "Su consultor",
  consultantAssigned: "Un consultor dedicado cuida de sus viajes. Su nombre y foto llegarán aquí pronto.",
  consultantNotYet: "Su consultor se asignará con su primer viaje.",
  write: "Escribir",
  whatsapp: "WhatsApp",
  call: "Llamar",
  installTitle: "Añada Mis viajes a su pantalla de inicio",
  installBody: "Un toque para sus viajes, documentos y ayuda, incluso en movimiento.",
  install: "Instalar",
  notNow: "Ahora no",
  reference: "Referencia",
  timeline: "Itinerario",
  timelineEmpty: "El plan día a día aparecerá aquí cuando su viaje esté confirmado.",
  services: "Servicios",
  servicesEmpty: "Aún no hay servicios.",
  travellersSection: "Viajeros",
  holder: "Titular de la reserva",
  companion: "Acompañante",
  inviteSoon: "Invitar acompañantes llegará pronto.",
  help: "Ayuda",
  helpBody: "Su consultor responde detrás de los canales de la marca, y nuestra línea está abierta 24 horas durante su viaje.",
  helpNow: "Ayuda ahora",
  helpNowSoon: "Disponible durante su viaje",
  legacyNote: "De nuestro sistema anterior; documentos bajo petición.",
  tripDocuments: "Sus documentos",
  reviewTitle: "¿Qué tal su estancia?",
  reviewInvite: "Cuente a otros viajeros cómo fue {hotel} ({dates}). Son dos minutos.",
  reviewWrite: "Escribir opinión",
  reviewOpenUntil: "Abierto hasta el {date}",
  legacyImported: "Importado de nuestro sistema anterior ({ref}). Su consultor lo gestiona aquí, como cualquier otro viaje.",
  document: "Documento",
  total: "Total",
  noDate: "Fechas por confirmar",
  flight: "Vuelo",
  pnr: "Código de reserva",
  terminal: "Terminal",
  checkIn: "Entrada",
  checkOut: "Salida",
  room: "Habitación",
  board: "Régimen",
  rooms: "{n} habitaciones",
  meetingPoint: "Punto de encuentro",
  vehicle: "Vehículo",
  flightNumber: "Vuelo",
  pickup: "Recogida",
  dropoff: "Entrega",
  duration: "Duración",
  supplier: "Proveedor",
  departure: "Salida",
  arrival: "Llegada",
  account: "Cuenta",
  yourDetails: "Sus datos",
  name: "Nombre",
  email: "Email",
  phone: "Móvil",
  consents: "Sus preferencias",
  consentsBody: "Solo le escribimos sobre sus viajes. Marque una marca para recibir también sus ofertas e inspiración.",
  marketingFrom: "Novedades y ofertas de {brand}",
  messagesSent: "Mensajes que le hemos enviado",
  messagesEmpty: "Nada todavía.",
  messagesStagingNote: "Este es un entorno de pruebas: los mensajes se registran aquí y no se entregan realmente.",
  openLink: "Abrir enlace",
  legal: "Legal",
  operatedBy: "Operado por {entity}",
  memberSince: "Cliente desde {date}",
  openingLink: "Abriendo su enlace…",
  linkUnknown: "No reconocemos este enlace.",
  linkExpired: "Este enlace ha caducado.",
  linkRevoked: "Este enlace ya no es válido.",
  linkBody: "Entre con un código para ver sus viajes.",
  helpSoon: "La ayuda inmediata estará disponible aquí muy pronto.",
  chooseBrand: "Elija su marca",
  chooseBrandBody: "Mis viajes está disponible en cada una de nuestras marcas. Abra aquella con la que reservó.",

  proposalEyebrow: "Su propuesta",
  preparedBy: "Preparada para usted por {name}",
  validUntil: "Válida hasta el {date}",
  aboutTrip: "Sobre este viaje",
  options: "Opciones",
  recommended: "Recomendada",
  chosen: "Elegida",
  selectOption: "Elegir esta opción",
  dayByDay: "Su viaje, día a día",
  dayN: "Día {n}",
  yourTotal: "Su total",
  earnsPoints: "Suma {points} puntos {programme}",
  depositLine: "Depósito de {deposit} al aceptar · resto {balance} más adelante",
  depositTerms: "Depósito al aceptar, resto más adelante",
  questions: "Preguntas a su consultor",
  questionsEmpty: "¿Alguna duda? Pregunte aquí y su consultor responde en esta página.",
  answerSoon: "Su consultor responderá pronto",
  askPlaceholder: "Escriba su pregunta…",
  ask: "Preguntar",
  acceptPay: "Aceptar y pagar el depósito",
  preContractTitle: "Antes de aceptar: información precontractual",
  preContractLead: "Este es un viaje combinado. La ley nos pide que le entreguemos esta información normalizada antes del pago: quién organiza su viaje, sus derechos principales y quién le protege en caso de insolvencia.",
  preContractOpen: "Leer la información precontractual",
  preContractCheck: "He leído la información precontractual y la acepto.",
  preContractRequired: "Marque la casilla para confirmar que ha leído la información precontractual.",
  preContractChanged: "La propuesta ha cambiado desde que la abrió. Lea la información actualizada y marque de nuevo.",
  preContractAccepted: "Información precontractual (versión {version}), aceptada el {date}",
  accepting: "Un momento…",
  decline: "Ahora no / Rechazar",
  declineConfirm: "¿Rechazar esta propuesta? Se informará a su consultor.",
  declineReason: "Cuéntenos por qué (opcional)",
  confirmDecline: "Sí, rechazar",
  proposalAccepted: "Ha aceptado esta propuesta",
  proposalAcceptedBody: "Su viaje está listo; el depósito le espera en su página.",
  proposalDeclined: "Esta propuesta fue rechazada",
  proposalDeclinedBody: "Si cambia de opinión, su consultor está a un mensaje de distancia.",
  proposalExpired: "Esta propuesta ha caducado",
  proposalExpiredBody: "Pida a su consultor una propuesta actualizada.",
  proposalClosed: "Esta propuesta está cerrada.",
  proposalNotYours: "Esta propuesta pertenece a otro cliente. Entre con la dirección a la que se envió.",
  proposalSignIn: "Entre para abrir su propuesta.",
  viewProposal: "Ver propuesta",
  welcomeTrip: "Bienvenido a su viaje",
  welcomeTripBody: "Pague el depósito abajo y empezamos a reservarlo todo.",
  payments: "Pagos",
  deposit: "Depósito",
  balance: "Resto",
  instalmentN: "Plazo {n}",
  refund: "Reembolso",
  balanceDueOn: "Resto a pagar antes del {date}",
  paidOf: "{paid} pagados de {total}",
  outstanding: "Pendiente",
  dueOn: "Vence el {date}",
  payAmount: "Pagar {amount}",
  payNow: "Pagar ahora",
  paying: "Procesando…",
  paymentMethod: "Método de pago",
  methodCard: "Tarjeta",
  methodMbway: "MB Way",
  methodTransfer: "Transferencia bancaria",
  methodApplePay: "Apple Pay",
  methodGooglePay: "Google Pay",
  simulation: "Simulación (entorno de pruebas)",
  simulationBody: "Aquí no se mueve dinero. Elija cómo debe terminar el pago.",
  simApprove: "Aprobar pago",
  simDecline: "Rechazar tarjeta",
  simPending: "Dejar pendiente (transferencia)",
  payPaid: "Pago recibido",
  payPaidBody: "Gracias. Su recibo está en sus mensajes.",
  payFailed: "El pago no se ha completado",
  payFailedBody: "Inténtelo de nuevo o elija otro método.",
  payPending: "Pago pendiente",
  payPendingBody: "Lo confirmamos en cuanto llegue la transferencia.",
  errAlreadyPaid: "Este pago ya se realizó.",
  errBadState: "Este pago no puede realizarse ahora mismo.",
  errInstalments: "No es posible pagar este resto a plazos.",
  errHolderOnly: "Solo el titular de la reserva puede pagar.",
  payMonthly: "Pagar el resto a plazos",
  payMonthlyBody: "Divida el resto en pagos mensuales, todos antes de la fecha límite.",
  monthsTimes: "{months} × {monthly}",
  firstLast: "Primero {first} · último {last}",
  instalmentsChosen: "Resto en {n} pagos mensuales",
  companionReadOnly: "El titular de la reserva gestiona los pagos.",
  noPlanYet: "Los detalles de pago llegan cuando la reserva esté preparada.",
  statusDue: "Por pagar",
  statusPaid: "Pagado",
  statusFailed: "Fallido",
  statusRefunded: "Reembolsado",
  planDepositDue: "Depósito por pagar",
  planDepositPaid: "Depósito pagado",
  planBalanceDue: "Resto por pagar",
  planPaid: "Pagado en su totalidad",
  planOverdue: "Atrasado",
  planRefunded: "Reembolsado",
  travellerDetails: "Datos de los viajeros",
  travellersDueBy: "Datos de los viajeros antes del {date}",
  travellerN: "Viajero {n}",
  trMissing: "Faltan",
  trSubmitted: "Enviados",
  trValidated: "Validados",
  trRejected: "Por corregir",
  enterDetails: "Introducir datos",
  edit: "Editar",
  givenNames: "Nombre(s)",
  surname: "Apellidos",
  dateOfBirth: "Fecha de nacimiento",
  nationality: "Nacionalidad",
  otherCountry: "Otro país (código de 2 letras)",
  countryCode: "Código de país",
  documentType: "Documento",
  passport: "Pasaporte",
  idCard: "DNI / documento de identidad",
  documentNumber: "Número de documento",
  documentExpiry: "Fecha de caducidad",
  gender: "Género (opcional)",
  genderF: "Femenino",
  genderM: "Masculino",
  genderX: "Otro / prefiero no decirlo",
  docsNote: "Los documentos nunca se guardan como imagen; solo estos campos.",
  errAlreadyValidated: "Este viajero ya fue validado; pida el cambio a su consultor.",
  rejectedReason: "Motivo: {reason}",
  travellerSaved: "Datos guardados. Su consultor los revisará.",
  requests: "Cambios y cancelaciones",
  requestsBody: "Diga a su consultor lo que necesita; la referencia va con el mensaje.",
  requestChange: "Solicitar un cambio",
  requestCancellation: "Solicitar una cancelación",
  changeSubject: "Solicitud de cambio · {ref}",
  cancelSubject: "Solicitud de cancelación · {ref}",
  payDeposit: "Pagar el depósito",
  enterTravellers: "Introducir datos de los viajeros",
  balanceDueHint: "Resto a pagar antes del {date}",
  // C3: conversation, assistant, requests
  navMessages: "Mensajes",
  unread: "Mensajes nuevos",
  conversation: "Conversación",
  conversationLead: "Sus mensajes con {name}, por email, WhatsApp y aquí.",
  conversationEmpty: "Escriba a {name}; el asistente responde enseguida y su consultor da seguimiento.",
  yourConsultantLower: "su consultor",
  assistant: "Asistente",
  channelHere: "Aquí",
  kindChangeRequest: "Solicitud de cambio",
  kindCancellationRequest: "Solicitud de cancelación",
  kindQuestion: "Pregunta",
  kindHandoff: "Pasado a su consultor",
  writePlaceholder: "Escriba su mensaje…",
  send: "Enviar",
  replyMeOn: "Responderme por",
  replyHere: "Aquí en la app",
  consultantTold: "Su consultor ha sido avisado.",
  assistantNote: "Respondo al instante; su consultor toma el relevo cuando pida hablar con una persona.",
  assistantOpen: "Preguntar al asistente",
  assistantEmpty: "Pregunte por sus documentos, el plan de mañana o cualquier detalle del viaje.",
  assistantPlaceholder: "Pregunte lo que quiera sobre su viaje…",
  assistantThinking: "Pensando…",
  chipDocuments: "¿Dónde están mis documentos?",
  chipTomorrow: "¿Cuál es el plan para mañana?",
  chipPerson: "Hablar con una persona",
  requestsNote: "Su consultor confirma las condiciones con los proveedores y vuelve con el coste exacto antes de cambiar nada.",
  requestChangePlaceholder: "¿Qué le gustaría cambiar? Fechas, viajeros, hotel, vuelos…",
  requestCancelPlaceholder: "¿Qué le gustaría cancelar, y a partir de cuándo?",
  requestSent: "Solicitud enviada",
  requestSentBody: "Su consultor responde en su conversación.",
  openConversation: "Abrir la conversación",
  newRequest: "Hacer otra solicitud",
  errHolderOnlyRequest: "Solo el titular de la reserva puede solicitar cambios o cancelaciones.",
  replyChannel: "Canal de respuesta",
  replyChannelBody: "Dónde le llegan las respuestas de su consultor cuando escribe desde aquí.",
  writeToMe: "Escribirme",
  speaks: "Habla: {langs}",
  // C4: trip companion
  scheduleChanged: "Horario modificado",
  wasTimes: "era",
  statusDriverAssigned: "Conductor asignado",
  statusEnRoute: "En camino",
  driver: "Conductor",
  plate: "Matrícula",
  callDriver: "Llamar al conductor",
  followLive: "Seguir el coche en directo",
  driverOnTheWay: "Su conductor está en camino",
  nextUp: "Siguiente",
  companions: "Acompañantes",
  companionsBody: "Los acompañantes ven el plan, los documentos y reciben los avisos; solo usted paga y habla por la reserva.",
  companionsReadOnly: "El titular de la reserva gestiona los acompañantes.",
  invited: "Invitado",
  joined: "Se ha unido",
  inviteCompanion: "Invitar a un acompañante",
  nameOptional: "Nombre (opcional)",
  invite: "Invitar",
  inviting: "Invitando…",
  remove: "Quitar",
  removeCompanionConfirm: "¿Quitar a {name} de este viaje?",
  errAlreadyMember: "Esta persona ya forma parte del viaje.",
  errTooManyCompanions: "Este viaje ha alcanzado el límite de acompañantes.",
  errHolderOnlyCompanions: "Solo el titular de la reserva puede invitar acompañantes.",
  tripNotices: "Avisos del viaje",
  tripNoticesBody: "Cuándo le escribimos sobre sus viajes y por dónde lo intentamos primero.",
  noticesBefore: "Antes del viaje",
  noticesBeforeHint: "Un resumen una semana antes y el plan de mañana la víspera.",
  noticesDuring: "Durante el viaje",
  noticesDuringHint: "El plan de cada mañana, cambios de vuelos y su conductor.",
  noticesAfter: "Después del viaje",
  noticesAfterHint: "Cómo fue y los puntos que ganó.",
  channelOrder: "Por dónde le contactamos primero",
  channelPush: "Notificación en este dispositivo",
  moveUp: "Subir",
  moveDown: "Bajar",
  thisDevice: "Este dispositivo",
  enablePush: "Activar notificaciones en este dispositivo",
  enablingPush: "Activando…",
  pushEnabled: "Las notificaciones están activas en este dispositivo.",
  pushDenied: "Las notificaciones están bloqueadas para este sitio en los ajustes del navegador.",
  pushUnsupported: "Este navegador no muestra notificaciones. Añada la app a la pantalla de inicio e inténtelo desde allí.",
  pushSimulated: "El push se simula en este entorno: los avisos quedan visibles en sus mensajes.",
  pushFailed: "No pudimos activar las notificaciones. Inténtelo de nuevo.",
  noticesBannerTitle: "Reciba los avisos en su móvil",
  noticesBannerBody: "Instale la app y active las notificaciones: el plan de cada mañana y novedades en directo sobre vuelos y conductores.",
  noticesBannerAction: "Activar notificaciones",
  // C5: Getaways Club y Ayuda ahora
  navClub: "Club",
  clubEmptyTitle: "Su cuenta {programme} se abre con su primer viaje",
  clubEmptyBody: "Cada euro gastado con cualquiera de nuestras tres marcas suma puntos, y 100 puntos descuentan 1 euro en su próximo saldo.",
  yourTier: "Su nivel",
  progressToNext: "{pct}% para {tier}",
  topTier: "Está en el nivel más alto",
  worth: "vale {value}",
  howItWorks: "Cómo funciona",
  howEarn: "1 punto por cada euro o dólar gastado con cualquiera de nuestras tres marcas.",
  howRedeem: "100 puntos = 1 unidad de descuento en el pago del saldo.",
  howExpire: "Los puntos caducan 24 meses después de su último viaje.",
  clubBrandsNote: "Una sola cuenta en Azores Getaways, Portugal Getaways y Yes Getaways.",
  tiersTitle: "Niveles",
  current: "Actual",
  fromPoints: "Desde {points} puntos",
  fromFirstTrip: "Desde su primer viaje",
  viewClub: "Ver sus ventajas",
  usePoints: "Use sus puntos {programme}",
  redeemBody: "Tiene {points} puntos, por valor de {value}, para usar en este saldo.",
  pointsToUse: "Puntos a usar",
  applyPoints: "Aplicar puntos",
  applying: "Aplicando…",
  pointsApplied: "{points} puntos aplicados (−{value})",
  fewerPoints: "Menos puntos",
  morePoints: "Más puntos",
  errNoBalance: "Aún no tiene puntos para usar en este saldo.",
  errTooManyPoints: "Es más de lo que puede usar aquí. Elija un importe menor, en centenas.",
  errNothingDue: "No queda nada por pagar en este viaje.",
  errNotLinked: "Su cuenta de fidelización aún no está vinculada. Su consultor puede ayudarle.",
  helpNowBody: "Cuéntenos qué ocurre. El asistente responde al instante y nuestro equipo recibe la alerta.",
  helpWhat: "¿Qué ocurre?",
  helpWhatPlaceholder: "Describa lo sucedido en pocas palabras.",
  helpWhere: "¿Dónde está? (opcional)",
  helpWherePlaceholder: "Hotel, aeropuerto, calle…",
  helpCanCall: "Pueden llamarme",
  helpPhone: "Número de teléfono",
  helpPhoneHint: "Le devolvemos la llamada a este número.",
  helpUrgency: "¿Qué urgencia tiene?",
  urgencyUrgent: "Urgente",
  urgencyUrgentHint: "Necesito ayuda ahora: vuelo perdido, accidente, sin habitación.",
  urgencySoon: "Pronto",
  urgencySoonHint: "Hoy, pero puedo esperar un poco.",
  urgencyInfo: "Solo una pregunta",
  urgencyInfoHint: "Sin prisa, necesito información.",
  agentCalled: "Estamos llamando a {agent} ahora",
  lineAlerted: "Nuestra línea 24h ha sido alertada.",
  call24h: "Llamar a la línea 24h",
  viewInMessages: "Ver en Mensajes",
  previousCases: "Casos anteriores",
  openedAgo: "Abierto {when}",
  statusOpen: "Abierto",
  statusClosed: "Cerrado",
  helpSent: "Hemos recibido su solicitud",
  // C6: launch (privacy notice, consents, data requests)
  privacy: "Privacidad",
  privacyVersion: "Versión {version} · Responsable: {controller}",
  privacyBody: "Sus datos y sus decisiones. Ideastation es la responsable del tratamiento en las tres marcas; una cuenta, una política de privacidad.",
  readPrivacy: "Leer la política de privacidad",
  consentTitle: "Una familia de marcas, una cuenta",
  consentController: "Ideastation, Lda. es la responsable del tratamiento de sus datos en las tres marcas.",
  iUnderstand: "Entendido",
  consentMarketingTitle: "Recibir también novedades (opcional)",
  serviceAgreement: "Acuerdo de servicio",
  consentAccepted: "Aceptado {brand}",
  consentPending: "Aún no aceptado",
  yourData: "Sus datos",
  exportData: "Exportar mis datos",
  deleteAccount: "Eliminar mi cuenta",
  dataRequestsNote: "La exportación es un archivo JSON con todo lo que esta área guarda sobre usted. Eliminar la cuenta borra sus datos personales; los viajes permanecen en nuestros registros como documentos comerciales, sin su nombre.",
  exporting: "Preparando el archivo…",
  exported: "Archivo guardado",
  deleteAccountExplain: "Sus viajes permanecen en nuestros registros como documentos comerciales, sin su nombre. Le enviamos por correo un enlace de confirmación, válido 48 horas; la cuenta solo se elimina cuando lo abra.",
  deleteAccountConfirm: "Enviarme el enlace de confirmación",
  deleteRequested: "Le hemos enviado un correo de confirmación. Abra el enlace en las próximas 48 horas para eliminar la cuenta.",
  deleteConfirmTitle: "Eliminar su cuenta",
  deleteConfirmBody: "Esto elimina definitivamente su cuenta y sus datos personales. Los viajes permanecen en nuestros registros como documentos comerciales, sin su nombre.",
  deleteForGood: "Eliminar definitivamente",
  deleting: "Eliminando…",
  keepAccount: "Conservar mi cuenta",
  deleteDone: "Su cuenta ha sido eliminada.",
  deleteDoneBody: "Gracias por viajar con nosotros. Puede cerrar esta página.",
  deleteLinkExpired: "Este enlace ha caducado o ya se ha utilizado.",
};

const fr: Catalog = {
  appName: "Mes voyages",
  loading: "Chargement…",
  retry: "Réessayer",
  back: "Retour",
  close: "Fermer",
  save: "Enregistrer",
  saving: "Enregistrement…",
  saved: "Enregistré",
  cancel: "Annuler",
  comingSoon: "Bientôt disponible",
  somethingWrong: "Une erreur s'est produite. Veuillez réessayer.",
  offline: "Vous semblez être hors ligne.",
  notFoundTitle: "Cette page n'existe pas",
  notFoundBody: "L'adresse est peut-être obsolète. Vos voyages sont à portée de main.",
  goHome: "Voir mes voyages",
  navHome: "Voyages",
  navAccount: "Compte",
  signOut: "Se déconnecter",
  signIn: "Se connecter",
  signInTitle: "Mes voyages",
  signInLead: "Vos voyages, vos documents et votre conseiller, au même endroit.",
  stagingBanner: "Staging : les e-mails, SMS et WhatsApp sont simulés. Votre code de connexion est dans la boîte d'envoi.",
  stagingOutbox: "Ouvrir la boîte d'envoi",
  stagingTest: "Système de test : rien n'arrive dans une vraie boîte mail ni sur un vrai téléphone. Votre code de connexion est dans la boîte d'envoi.",
  continueWith: "Continuer avec {provider}",
  orDivider: "ou",
  socialFailed: "La connexion avec {provider} n'a pas abouti. Réessayez ou utilisez un code.",
  signInEmail: "E-mail",
  signInPhone: "Numéro de mobile",
  usePhone: "Utiliser mon mobile",
  useEmail: "Utiliser mon e-mail",
  emailPlaceholder: "vous@exemple.com",
  phonePlaceholder: "+33 6…",
  sendCode: "M'envoyer un code",
  sending: "Envoi…",
  codeSentTo: "Nous avons envoyé un code à {to}.",
  codeSentSms: "Nous avons envoyé un code par SMS au {to}.",
  enterCode: "Saisissez le code",
  codeHint: "Ou ouvrez le lien que nous vous avons envoyé : il vous connecte directement.",
  resend: "Envoyer un nouveau code",
  resendIn: "Envoyer un nouveau code dans {s} s",
  changeAddress: "Utiliser une autre adresse",
  verifying: "Vérification…",
  welcomeCreated: "Bienvenue ! Votre compte est prêt.",
  magicChecking: "Ouverture de votre lien…",
  magicFailed: "Ce lien ne fonctionne plus. Demandez un nouveau code ci-dessous.",
  language: "Langue",
  errInvalidAddress: "Cette adresse ne semble pas valide. Veuillez la vérifier.",
  errTooManyCodes: "Trop de codes demandés. Patientez quelques minutes puis réessayez.",
  errUnknownPhone: "Nous ne connaissons pas encore ce numéro. Connectez-vous avec votre e-mail la première fois ; vous pourrez ajouter le numéro ensuite.",
  errProviderUnavailable: "Nous ne pouvons pas envoyer de code pour le moment. Réessayez dans un instant.",
  errWrongCode: "Ce code est incorrect. Vérifiez-le et réessayez.",
  errChallengeExpired: "Ce code a expiré. Demandez-en un nouveau.",
  errTooManyAttempts: "Trop de tentatives. Demandez un nouveau code.",
  errNoBrand: "Cette adresse ne sert aucune marque. Ouvrez Mes voyages de votre marque.",
  greeting: "Bonjour, {name}",
  greetingNoName: "Bonjour",
  nextTrip: "Votre prochain voyage",
  inDays: "Dans {n} jours",
  tomorrow: "Demain",
  today: "Aujourd'hui",
  ongoing: "En cours",
  upcoming: "À venir également",
  pastTrips: "Voyages passés",
  noTrips: "Aucun voyage pour l'instant",
  noTripsBody: "Dès que vous réservez avec nous, votre voyage apparaît ici avec son itinéraire et ses documents.",
  proposalWaiting: "Votre proposition vous attend",
  viewTrip: "Voir le voyage",
  travellers: "{n} voyageurs",
  traveller: "1 voyageur",
  linkSessionNote: "Vous avez ouvert un lien vers un voyage. Connectez-vous avec un code pour voir tous vos voyages.",
  statusProposal: "Proposition",
  statusPending: "En attente",
  statusConfirmed: "Confirmé",
  statusInProgress: "En cours",
  statusCompleted: "Terminé",
  statusCancelled: "Annulé",
  points: "points",
  tier: "Niveau",
  joinClub: "Rejoignez {programme}",
  joinClubBody: "Gagnez des points à chaque voyage avec l'une de nos marques et profitez d'avantages en voyageant. Votre conseiller vous inscrira lors de votre premier voyage.",
  pointsExpire: "Les points expirent le {date}",
  yourConsultant: "Votre conseiller",
  consultantAssigned: "Un conseiller dédié s'occupe de vos voyages. Son nom et sa photo arriveront ici bientôt.",
  consultantNotYet: "Votre conseiller vous sera attribué lors de votre premier voyage.",
  write: "Écrire",
  whatsapp: "WhatsApp",
  call: "Appeler",
  installTitle: "Ajoutez Mes voyages à votre écran d'accueil",
  installBody: "Un geste pour vos voyages, vos documents et de l'aide, même en déplacement.",
  install: "Installer",
  notNow: "Plus tard",
  reference: "Référence",
  timeline: "Itinéraire",
  timelineEmpty: "Le programme jour par jour apparaîtra ici dès que votre voyage sera confirmé.",
  services: "Prestations",
  servicesEmpty: "Aucune prestation pour l'instant.",
  travellersSection: "Voyageurs",
  holder: "Titulaire de la réservation",
  companion: "Accompagnant",
  inviteSoon: "L'invitation d'accompagnants arrive bientôt.",
  help: "Aide",
  helpBody: "Votre conseiller répond derrière les canaux de la marque, et notre ligne est ouverte 24 h/24 pendant votre voyage.",
  helpNow: "Aide immédiate",
  helpNowSoon: "Disponible pendant votre voyage",
  legacyNote: "Issu de notre ancien système ; documents sur demande.",
  tripDocuments: "Vos documents",
  reviewTitle: "Comment s'est passé votre séjour ?",
  reviewInvite: "Racontez aux autres voyageurs votre séjour à {hotel} ({dates}). Cela prend deux minutes.",
  reviewWrite: "Donner mon avis",
  reviewOpenUntil: "Ouvert jusqu'au {date}",
  legacyImported: "Importé de notre ancien système ({ref}). Votre conseiller le gère ici, comme tout autre voyage.",
  document: "Document",
  total: "Total",
  noDate: "Dates à confirmer",
  flight: "Vol",
  pnr: "Code de réservation",
  terminal: "Terminal",
  checkIn: "Arrivée",
  checkOut: "Départ",
  room: "Chambre",
  board: "Pension",
  rooms: "{n} chambres",
  meetingPoint: "Point de rendez-vous",
  vehicle: "Véhicule",
  flightNumber: "Vol",
  pickup: "Prise en charge",
  dropoff: "Dépose",
  duration: "Durée",
  supplier: "Prestataire",
  departure: "Départ",
  arrival: "Arrivée",
  account: "Compte",
  yourDetails: "Vos informations",
  name: "Nom",
  email: "E-mail",
  phone: "Numéro de mobile",
  consents: "Vos choix",
  consentsBody: "Nous ne vous écrivons qu'au sujet de vos voyages. Cochez une marque pour recevoir aussi ses offres et son inspiration.",
  marketingFrom: "Nouveautés et offres de {brand}",
  messagesSent: "Messages que nous vous avons envoyés",
  messagesEmpty: "Rien pour l'instant.",
  messagesStagingNote: "Ceci est un environnement de test : les messages sont enregistrés ici et ne sont pas réellement envoyés.",
  openLink: "Ouvrir le lien",
  legal: "Mentions légales",
  operatedBy: "Exploité par {entity}",
  memberSince: "Client depuis {date}",
  openingLink: "Ouverture de votre lien…",
  linkUnknown: "Nous ne reconnaissons pas ce lien.",
  linkExpired: "Ce lien a expiré.",
  linkRevoked: "Ce lien n'est plus valide.",
  linkBody: "Connectez-vous avec un code pour voir vos voyages.",
  helpSoon: "L'aide immédiate sera disponible ici très bientôt.",
  chooseBrand: "Choisissez votre marque",
  chooseBrandBody: "Mes voyages est disponible sur chacune de nos marques. Ouvrez celle avec laquelle vous avez réservé.",

  proposalEyebrow: "Votre proposition",
  preparedBy: "Préparée pour vous par {name}",
  validUntil: "Valable jusqu'au {date}",
  aboutTrip: "À propos de ce voyage",
  options: "Options",
  recommended: "Recommandée",
  chosen: "Choisie",
  selectOption: "Choisir cette option",
  dayByDay: "Votre voyage, jour par jour",
  dayN: "Jour {n}",
  yourTotal: "Votre total",
  earnsPoints: "Rapporte {points} points {programme}",
  depositLine: "Acompte de {deposit} à l'acceptation · solde {balance} plus tard",
  depositTerms: "Acompte à l'acceptation, solde plus tard",
  questions: "Questions à votre conseiller",
  questionsEmpty: "Une question ? Posez-la ici et votre conseiller répond sur cette page.",
  answerSoon: "Votre conseiller répondra bientôt",
  askPlaceholder: "Écrivez votre question…",
  ask: "Demander",
  acceptPay: "Accepter et payer l'acompte",
  preContractTitle: "Avant d'accepter : informations précontractuelles",
  preContractLead: "Il s'agit d'un voyage à forfait. La loi nous demande de vous remettre ces informations standard avant le paiement : qui organise votre voyage, vos principaux droits et qui vous protège en cas d'insolvabilité.",
  preContractOpen: "Lire les informations précontractuelles",
  preContractCheck: "J'ai lu les informations précontractuelles et je les accepte.",
  preContractRequired: "Cochez la case pour confirmer que vous avez lu les informations précontractuelles.",
  preContractChanged: "La proposition a changé depuis son ouverture. Lisez les informations mises à jour et cochez à nouveau.",
  preContractAccepted: "Informations précontractuelles (version {version}), acceptées le {date}",
  accepting: "Un instant…",
  decline: "Pas maintenant / Refuser",
  declineConfirm: "Refuser cette proposition ? Votre conseiller en sera informé.",
  declineReason: "Dites-nous pourquoi (facultatif)",
  confirmDecline: "Oui, refuser",
  proposalAccepted: "Vous avez accepté cette proposition",
  proposalAcceptedBody: "Votre voyage est prêt ; l'acompte vous attend sur sa page.",
  proposalDeclined: "Cette proposition a été refusée",
  proposalDeclinedBody: "Si vous changez d'avis, votre conseiller est à un message de vous.",
  proposalExpired: "Cette proposition a expiré",
  proposalExpiredBody: "Demandez à votre conseiller une proposition à jour.",
  proposalClosed: "Cette proposition est close.",
  proposalNotYours: "Cette proposition appartient à un autre client. Connectez-vous avec l'adresse à laquelle elle a été envoyée.",
  proposalSignIn: "Connectez-vous pour ouvrir votre proposition.",
  viewProposal: "Voir la proposition",
  welcomeTrip: "Bienvenue dans votre voyage",
  welcomeTripBody: "Payez l'acompte ci-dessous et nous commençons à tout réserver.",
  payments: "Paiements",
  deposit: "Acompte",
  balance: "Solde",
  instalmentN: "Mensualité {n}",
  refund: "Remboursement",
  balanceDueOn: "Solde à régler avant le {date}",
  paidOf: "{paid} payés sur {total}",
  outstanding: "Restant dû",
  dueOn: "Échéance le {date}",
  payAmount: "Payer {amount}",
  payNow: "Payer maintenant",
  paying: "Traitement…",
  paymentMethod: "Moyen de paiement",
  methodCard: "Carte",
  methodMbway: "MB Way",
  methodTransfer: "Virement bancaire",
  methodApplePay: "Apple Pay",
  methodGooglePay: "Google Pay",
  simulation: "Simulation (environnement de test)",
  simulationBody: "Aucun argent ne circule ici. Choisissez l'issue du paiement.",
  simApprove: "Approuver le paiement",
  simDecline: "Refuser la carte",
  simPending: "Laisser en attente (virement)",
  payPaid: "Paiement reçu",
  payPaidBody: "Merci. Votre reçu est dans vos messages.",
  payFailed: "Le paiement n'a pas abouti",
  payFailedBody: "Réessayez ou choisissez un autre moyen.",
  payPending: "Paiement en attente",
  payPendingBody: "Nous le confirmons dès réception du virement.",
  errAlreadyPaid: "Ce paiement a déjà été effectué.",
  errBadState: "Ce paiement ne peut pas être effectué pour le moment.",
  errInstalments: "Le paiement mensuel n'est pas possible pour ce solde.",
  errHolderOnly: "Seul le titulaire de la réservation peut payer.",
  payMonthly: "Payer le solde en plusieurs fois",
  payMonthlyBody: "Répartissez le solde en mensualités, toutes avant la date limite.",
  monthsTimes: "{months} × {monthly}",
  firstLast: "Première le {first} · dernière le {last}",
  instalmentsChosen: "Solde en {n} mensualités",
  companionReadOnly: "Le titulaire de la réservation gère les paiements.",
  noPlanYet: "Les détails de paiement arrivent dès que la réservation est préparée.",
  statusDue: "À payer",
  statusPaid: "Payé",
  statusFailed: "Échoué",
  statusRefunded: "Remboursé",
  planDepositDue: "Acompte à payer",
  planDepositPaid: "Acompte payé",
  planBalanceDue: "Solde à payer",
  planPaid: "Entièrement payé",
  planOverdue: "En retard",
  planRefunded: "Remboursé",
  travellerDetails: "Données des voyageurs",
  travellersDueBy: "Données des voyageurs avant le {date}",
  travellerN: "Voyageur {n}",
  trMissing: "Manquantes",
  trSubmitted: "Envoyées",
  trValidated: "Validées",
  trRejected: "À corriger",
  enterDetails: "Saisir les données",
  edit: "Modifier",
  givenNames: "Prénoms",
  surname: "Nom",
  dateOfBirth: "Date de naissance",
  nationality: "Nationalité",
  otherCountry: "Autre pays (code à 2 lettres)",
  countryCode: "Code pays",
  documentType: "Document",
  passport: "Passeport",
  idCard: "Carte d'identité",
  documentNumber: "Numéro du document",
  documentExpiry: "Date d'expiration",
  gender: "Genre (facultatif)",
  genderF: "Féminin",
  genderM: "Masculin",
  genderX: "Autre / ne souhaite pas répondre",
  docsNote: "Les documents ne sont jamais stockés en image ; uniquement ces champs.",
  errAlreadyValidated: "Ce voyageur a été validé ; demandez la modification à votre conseiller.",
  rejectedReason: "Motif : {reason}",
  travellerSaved: "Données enregistrées. Votre conseiller va les vérifier.",
  requests: "Modifications et annulations",
  requestsBody: "Dites à votre conseiller ce dont vous avez besoin ; la référence accompagne le message.",
  requestChange: "Demander une modification",
  requestCancellation: "Demander une annulation",
  changeSubject: "Demande de modification · {ref}",
  cancelSubject: "Demande d'annulation · {ref}",
  payDeposit: "Payer l'acompte",
  enterTravellers: "Saisir les données des voyageurs",
  balanceDueHint: "Solde à régler avant le {date}",
  // C3: conversation, assistant, requests
  navMessages: "Messages",
  unread: "Nouveaux messages",
  conversation: "Conversation",
  conversationLead: "Vos messages avec {name}, par e-mail, WhatsApp et ici.",
  conversationEmpty: "Écrivez à {name} ; l'assistant répond tout de suite, votre conseiller prend la suite.",
  yourConsultantLower: "votre conseiller",
  assistant: "Assistant",
  channelHere: "Ici",
  kindChangeRequest: "Demande de modification",
  kindCancellationRequest: "Demande d'annulation",
  kindQuestion: "Question",
  kindHandoff: "Transmis à votre conseiller",
  writePlaceholder: "Écrivez votre message…",
  send: "Envoyer",
  replyMeOn: "Me répondre par",
  replyHere: "Ici dans l'app",
  consultantTold: "Votre conseiller a été prévenu.",
  assistantNote: "Je réponds instantanément ; votre conseiller prend le relais dès que vous demandez une personne.",
  assistantOpen: "Demander à l'assistant",
  assistantEmpty: "Demandez vos documents, le programme de demain ou tout détail de votre voyage.",
  assistantPlaceholder: "Posez une question sur votre voyage…",
  assistantThinking: "Je réfléchis…",
  chipDocuments: "Où sont mes documents ?",
  chipTomorrow: "Quel est le programme de demain ?",
  chipPerson: "Parler à une personne",
  requestsNote: "Votre conseiller vérifie les conditions auprès des prestataires et revient avec le coût exact avant tout changement.",
  requestChangePlaceholder: "Que souhaitez-vous modifier ? Dates, voyageurs, hôtel, vols…",
  requestCancelPlaceholder: "Que souhaitez-vous annuler, et à partir de quand ?",
  requestSent: "Demande envoyée",
  requestSentBody: "Votre conseiller répond dans votre conversation.",
  openConversation: "Ouvrir la conversation",
  newRequest: "Faire une autre demande",
  errHolderOnlyRequest: "Seul le titulaire de la réservation peut demander une modification ou une annulation.",
  replyChannel: "Canal de réponse",
  replyChannelBody: "Où les réponses de votre conseiller vous parviennent quand vous écrivez d'ici.",
  writeToMe: "M'écrire",
  speaks: "Parle : {langs}",
  // C4: trip companion
  scheduleChanged: "Horaire modifié",
  wasTimes: "était",
  statusDriverAssigned: "Chauffeur attribué",
  statusEnRoute: "En route",
  driver: "Chauffeur",
  plate: "Immatriculation",
  callDriver: "Appeler le chauffeur",
  followLive: "Suivre la voiture en direct",
  driverOnTheWay: "Votre chauffeur est en route",
  nextUp: "À suivre",
  companions: "Accompagnants",
  companionsBody: "Les accompagnants voient le programme, les documents et reçoivent les avis ; vous seul payez et parlez pour la réservation.",
  companionsReadOnly: "Le titulaire de la réservation gère les accompagnants.",
  invited: "Invité",
  joined: "A rejoint",
  inviteCompanion: "Inviter un accompagnant",
  nameOptional: "Nom (facultatif)",
  invite: "Inviter",
  inviting: "Invitation…",
  remove: "Retirer",
  removeCompanionConfirm: "Retirer {name} de ce voyage ?",
  errAlreadyMember: "Cette personne fait déjà partie du voyage.",
  errTooManyCompanions: "Ce voyage a atteint sa limite d'accompagnants.",
  errHolderOnlyCompanions: "Seul le titulaire de la réservation peut inviter des accompagnants.",
  tripNotices: "Avis de voyage",
  tripNoticesBody: "Quand nous vous écrivons au sujet de vos voyages, et par où nous essayons d'abord.",
  noticesBefore: "Avant le voyage",
  noticesBeforeHint: "Un résumé une semaine avant et le programme du lendemain la veille.",
  noticesDuring: "Pendant le voyage",
  noticesDuringHint: "Le programme de chaque matin, les changements de vol et votre chauffeur.",
  noticesAfter: "Après le voyage",
  noticesAfterHint: "Comment cela s'est passé et les points gagnés.",
  channelOrder: "Par où nous vous joignons d'abord",
  channelPush: "Notification sur cet appareil",
  moveUp: "Monter",
  moveDown: "Descendre",
  thisDevice: "Cet appareil",
  enablePush: "Activer les notifications sur cet appareil",
  enablingPush: "Activation…",
  pushEnabled: "Les notifications sont actives sur cet appareil.",
  pushDenied: "Les notifications sont bloquées pour ce site dans les réglages du navigateur.",
  pushUnsupported: "Ce navigateur n'affiche pas de notifications. Ajoutez l'app à l'écran d'accueil et réessayez depuis là.",
  pushSimulated: "Le push est simulé dans cet environnement : les avis sont visibles dans vos messages.",
  pushFailed: "Impossible d'activer les notifications. Veuillez réessayer.",
  noticesBannerTitle: "Recevez les avis sur votre téléphone",
  noticesBannerBody: "Installez l'app et activez les notifications : le programme de chaque matin et les nouvelles en direct sur vols et chauffeurs.",
  noticesBannerAction: "Activer les notifications",
  // C5 : Getaways Club et Aide immédiate
  navClub: "Club",
  clubEmptyTitle: "Votre compte {programme} s'ouvre avec votre premier voyage",
  clubEmptyBody: "Chaque euro dépensé auprès de l'une de nos trois marques rapporte des points, et 100 points réduisent votre prochain solde de 1 euro.",
  yourTier: "Votre niveau",
  progressToNext: "{pct} % vers {tier}",
  topTier: "Vous êtes au niveau le plus élevé",
  worth: "soit {value}",
  howItWorks: "Comment ça marche",
  howEarn: "1 point par euro ou dollar dépensé auprès de l'une de nos trois marques.",
  howRedeem: "100 points = 1 unité de réduction sur le paiement du solde.",
  howExpire: "Les points expirent 24 mois après votre dernier voyage.",
  clubBrandsNote: "Un seul compte pour Azores Getaways, Portugal Getaways et Yes Getaways.",
  tiersTitle: "Niveaux",
  current: "Actuel",
  fromPoints: "À partir de {points} points",
  fromFirstTrip: "Dès votre premier voyage",
  viewClub: "Voir vos avantages",
  usePoints: "Utilisez vos points {programme}",
  redeemBody: "Vous avez {points} points, soit {value}, à utiliser sur ce solde.",
  pointsToUse: "Points à utiliser",
  applyPoints: "Appliquer les points",
  applying: "Application…",
  pointsApplied: "{points} points appliqués (−{value})",
  fewerPoints: "Moins de points",
  morePoints: "Plus de points",
  errNoBalance: "Vous n'avez pas encore de points à utiliser sur ce solde.",
  errTooManyPoints: "C'est plus que ce que vous pouvez utiliser ici. Choisissez un montant inférieur, par centaines.",
  errNothingDue: "Il ne reste rien à payer pour ce voyage.",
  errNotLinked: "Votre compte de fidélité n'est pas encore lié. Votre conseiller peut vous aider.",
  helpNowBody: "Dites-nous ce qui ne va pas. L'assistant répond aussitôt et notre équipe est alertée.",
  helpWhat: "Que se passe-t-il ?",
  helpWhatPlaceholder: "Décrivez ce qui s'est passé en quelques mots.",
  helpWhere: "Où êtes-vous ? (facultatif)",
  helpWherePlaceholder: "Hôtel, aéroport, rue…",
  helpCanCall: "Vous pouvez m'appeler",
  helpPhone: "Numéro de téléphone",
  helpPhoneHint: "Nous vous rappelons à ce numéro.",
  helpUrgency: "Quelle urgence ?",
  urgencyUrgent: "Urgent",
  urgencyUrgentHint: "J'ai besoin d'aide tout de suite : vol manqué, accident, pas de chambre.",
  urgencySoon: "Bientôt",
  urgencySoonHint: "Aujourd'hui, mais je peux attendre un peu.",
  urgencyInfo: "Juste une question",
  urgencyInfoHint: "Rien de pressé, j'ai besoin d'une information.",
  agentCalled: "Nous appelons {agent} en ce moment",
  lineAlerted: "Notre ligne 24 h/24 a été alertée.",
  call24h: "Appeler la ligne 24 h/24",
  viewInMessages: "Voir dans Messages",
  previousCases: "Demandes précédentes",
  openedAgo: "Ouverte {when}",
  statusOpen: "Ouverte",
  statusClosed: "Fermée",
  helpSent: "Nous avons bien reçu votre demande",
  // C6: launch (privacy notice, consents, data requests)
  privacy: "Confidentialité",
  privacyVersion: "Version {version} · Responsable : {controller}",
  privacyBody: "Vos données et vos choix. Ideastation est le responsable du traitement pour les trois marques ; un compte, une politique de confidentialité.",
  readPrivacy: "Lire la politique de confidentialité",
  consentTitle: "Une famille de marques, un seul compte",
  consentController: "Ideastation, Lda. est le responsable du traitement de vos données pour les trois marques.",
  iUnderstand: "J'ai compris",
  consentMarketingTitle: "Recevoir aussi nos actualités (facultatif)",
  serviceAgreement: "Accord de service",
  consentAccepted: "Accepté {brand}",
  consentPending: "Pas encore accepté",
  yourData: "Vos données",
  exportData: "Exporter mes données",
  deleteAccount: "Supprimer mon compte",
  dataRequestsNote: "L'export est un fichier JSON contenant tout ce que cet espace conserve à votre sujet. Supprimer votre compte efface vos données personnelles ; vos voyages restent dans nos registres comme documents commerciaux, sans votre nom.",
  exporting: "Préparation du fichier…",
  exported: "Fichier enregistré",
  deleteAccountExplain: "Vos voyages restent dans nos registres comme documents commerciaux, sans votre nom. Nous vous envoyons par e-mail un lien de confirmation, valable 48 heures ; votre compte n'est supprimé que lorsque vous l'ouvrez.",
  deleteAccountConfirm: "M'envoyer le lien de confirmation",
  deleteRequested: "Nous vous avons envoyé un e-mail de confirmation. Ouvrez le lien dans les 48 heures pour supprimer votre compte.",
  deleteConfirmTitle: "Supprimer votre compte",
  deleteConfirmBody: "Cette action efface définitivement votre compte et vos données personnelles. Vos voyages restent dans nos registres comme documents commerciaux, sans votre nom.",
  deleteForGood: "Supprimer définitivement",
  deleting: "Suppression…",
  keepAccount: "Garder mon compte",
  deleteDone: "Votre compte a été supprimé.",
  deleteDoneBody: "Merci d'avoir voyagé avec nous. Vous pouvez fermer cette page.",
  deleteLinkExpired: "Ce lien a expiré ou a déjà été utilisé.",
};

const de: Catalog = {
  appName: "Meine Reisen",
  loading: "Wird geladen…",
  retry: "Erneut versuchen",
  back: "Zurück",
  close: "Schließen",
  save: "Speichern",
  saving: "Wird gespeichert…",
  saved: "Gespeichert",
  cancel: "Abbrechen",
  comingSoon: "Demnächst",
  somethingWrong: "Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.",
  offline: "Sie scheinen offline zu sein.",
  notFoundTitle: "Diese Seite gibt es nicht",
  notFoundBody: "Die Adresse ist vielleicht veraltet. Ihre Reisen sind nur einen Tipp entfernt.",
  goHome: "Zu meinen Reisen",
  navHome: "Reisen",
  navAccount: "Konto",
  signOut: "Abmelden",
  signIn: "Anmelden",
  signInTitle: "Meine Reisen",
  signInLead: "Ihre Reisen, Dokumente und Ihr Berater an einem Ort.",
  stagingBanner: "Staging: E-Mails, SMS und WhatsApp werden simuliert. Ihr Anmeldecode liegt im Postausgang.",
  stagingOutbox: "Postausgang öffnen",
  stagingTest: "Testsystem: Nichts erreicht ein echtes Postfach oder Telefon. Ihr Anmeldecode liegt im Postausgang.",
  continueWith: "Weiter mit {provider}",
  orDivider: "oder",
  socialFailed: "Die Anmeldung mit {provider} wurde nicht abgeschlossen. Versuchen Sie es erneut oder nutzen Sie einen Code.",
  signInEmail: "E-Mail",
  signInPhone: "Mobilnummer",
  usePhone: "Meine Mobilnummer verwenden",
  useEmail: "Meine E-Mail verwenden",
  emailPlaceholder: "sie@beispiel.de",
  phonePlaceholder: "+49 15…",
  sendCode: "Code zusenden",
  sending: "Wird gesendet…",
  codeSentTo: "Wir haben einen Code an {to} gesendet.",
  codeSentSms: "Wir haben einen Code per SMS an {to} gesendet.",
  enterCode: "Code eingeben",
  codeHint: "Oder öffnen Sie den Link, den wir Ihnen geschickt haben: er meldet Sie direkt an.",
  resend: "Neuen Code senden",
  resendIn: "Neuen Code senden in {s} s",
  changeAddress: "Andere Adresse verwenden",
  verifying: "Wird geprüft…",
  welcomeCreated: "Willkommen! Ihr Konto ist bereit.",
  magicChecking: "Ihr Link wird geöffnet…",
  magicFailed: "Dieser Link funktioniert nicht mehr. Fordern Sie unten einen neuen Code an.",
  language: "Sprache",
  errInvalidAddress: "Diese Adresse scheint ungültig zu sein. Bitte prüfen Sie sie.",
  errTooManyCodes: "Zu viele Codes angefordert. Bitte warten Sie einige Minuten und versuchen Sie es erneut.",
  errUnknownPhone: "Diese Nummer kennen wir noch nicht. Melden Sie sich beim ersten Mal mit Ihrer E-Mail an; die Nummer können Sie danach hinzufügen.",
  errProviderUnavailable: "Wir können derzeit keine Codes senden. Bitte versuchen Sie es gleich noch einmal.",
  errWrongCode: "Dieser Code ist nicht richtig. Bitte prüfen und erneut versuchen.",
  errChallengeExpired: "Dieser Code ist abgelaufen. Fordern Sie einen neuen an.",
  errTooManyAttempts: "Zu viele Versuche. Fordern Sie einen neuen Code an.",
  errNoBrand: "Diese Adresse gehört zu keiner Marke. Öffnen Sie Meine Reisen Ihrer Marke.",
  greeting: "Hallo, {name}",
  greetingNoName: "Hallo",
  nextTrip: "Ihre nächste Reise",
  inDays: "In {n} Tagen",
  tomorrow: "Morgen",
  today: "Heute",
  ongoing: "Läuft gerade",
  upcoming: "Ebenfalls geplant",
  pastTrips: "Vergangene Reisen",
  noTrips: "Noch keine Reisen",
  noTripsBody: "Sobald Sie bei uns buchen, erscheint Ihre Reise hier mit Reiseplan und Dokumenten.",
  proposalWaiting: "Ihr Angebot wartet auf Sie",
  viewTrip: "Reise ansehen",
  travellers: "{n} Reisende",
  traveller: "1 Reisender",
  linkSessionNote: "Sie haben einen Link zu einer Reise geöffnet. Melden Sie sich mit einem Code an, um alle Ihre Reisen zu sehen.",
  statusProposal: "Angebot",
  statusPending: "Ausstehend",
  statusConfirmed: "Bestätigt",
  statusInProgress: "Unterwegs",
  statusCompleted: "Abgeschlossen",
  statusCancelled: "Storniert",
  points: "Punkte",
  tier: "Stufe",
  joinClub: "Werden Sie Mitglied bei {programme}",
  joinClubBody: "Sammeln Sie Punkte auf jeder Reise mit einer unserer Marken und genießen Sie Vorteile unterwegs. Ihr Berater meldet Sie mit Ihrer ersten Reise an.",
  pointsExpire: "Die Punkte verfallen am {date}",
  yourConsultant: "Ihr Berater",
  consultantAssigned: "Ein persönlicher Berater kümmert sich um Ihre Reisen. Name und Foto erscheinen hier bald.",
  consultantNotYet: "Ihr Berater wird Ihnen mit Ihrer ersten Reise zugewiesen.",
  write: "Schreiben",
  whatsapp: "WhatsApp",
  call: "Anrufen",
  installTitle: "Meine Reisen zum Startbildschirm hinzufügen",
  installBody: "Ein Tipp zu Ihren Reisen, Dokumenten und Hilfe, auch unterwegs.",
  install: "Installieren",
  notNow: "Jetzt nicht",
  reference: "Referenz",
  timeline: "Reiseplan",
  timelineEmpty: "Der Tagesplan erscheint hier, sobald Ihre Reise bestätigt ist.",
  services: "Leistungen",
  servicesEmpty: "Noch keine Leistungen aufgeführt.",
  travellersSection: "Reisende",
  holder: "Buchungsinhaber",
  companion: "Mitreisender",
  inviteSoon: "Mitreisende einladen kommt bald.",
  help: "Hilfe",
  helpBody: "Ihr Berater antwortet über die Kanäle der Marke, und unsere Leitung ist während Ihrer Reise rund um die Uhr besetzt.",
  helpNow: "Sofort Hilfe",
  helpNowSoon: "Während Ihrer Reise verfügbar",
  legacyNote: "Aus unserem früheren System; Dokumente auf Anfrage.",
  tripDocuments: "Ihre Dokumente",
  reviewTitle: "Wie war Ihr Aufenthalt?",
  reviewInvite: "Erzählen Sie anderen Reisenden von {hotel} ({dates}). Es dauert zwei Minuten.",
  reviewWrite: "Bewertung schreiben",
  reviewOpenUntil: "Offen bis {date}",
  legacyImported: "Aus unserem früheren System übernommen ({ref}). Ihr Berater betreut die Reise hier wie jede andere.",
  document: "Dokument",
  total: "Gesamt",
  noDate: "Termine werden bestätigt",
  flight: "Flug",
  pnr: "Buchungscode",
  terminal: "Terminal",
  checkIn: "Check-in",
  checkOut: "Check-out",
  room: "Zimmer",
  board: "Verpflegung",
  rooms: "{n} Zimmer",
  meetingPoint: "Treffpunkt",
  vehicle: "Fahrzeug",
  flightNumber: "Flug",
  pickup: "Abholung",
  dropoff: "Ankunft",
  duration: "Dauer",
  supplier: "Anbieter",
  departure: "Abflug",
  arrival: "Ankunft",
  account: "Konto",
  yourDetails: "Ihre Angaben",
  name: "Name",
  email: "E-Mail",
  phone: "Mobilnummer",
  consents: "Ihre Einstellungen",
  consentsBody: "Wir schreiben Ihnen nur zu Ihren Reisen. Wählen Sie eine Marke, um auch ihre Angebote und Inspiration zu erhalten.",
  marketingFrom: "Neuigkeiten und Angebote von {brand}",
  messagesSent: "Nachrichten, die wir Ihnen geschickt haben",
  messagesEmpty: "Noch nichts.",
  messagesStagingNote: "Dies ist eine Testumgebung: Nachrichten werden hier aufgezeichnet und nicht tatsächlich zugestellt.",
  openLink: "Link öffnen",
  legal: "Rechtliches",
  operatedBy: "Betrieben von {entity}",
  memberSince: "Kunde seit {date}",
  openingLink: "Ihr Link wird geöffnet…",
  linkUnknown: "Diesen Link kennen wir nicht.",
  linkExpired: "Dieser Link ist abgelaufen.",
  linkRevoked: "Dieser Link ist nicht mehr gültig.",
  linkBody: "Melden Sie sich mit einem Code an, um Ihre Reisen zu sehen.",
  helpSoon: "Sofortige Hilfe ist hier sehr bald verfügbar.",
  chooseBrand: "Wählen Sie Ihre Marke",
  chooseBrandBody: "Meine Reisen gibt es bei jeder unserer Marken. Öffnen Sie die, bei der Sie gebucht haben.",

  proposalEyebrow: "Ihr Angebot",
  preparedBy: "Für Sie zusammengestellt von {name}",
  validUntil: "Gültig bis {date}",
  aboutTrip: "Über diese Reise",
  options: "Optionen",
  recommended: "Empfohlen",
  chosen: "Gewählt",
  selectOption: "Diese Option wählen",
  dayByDay: "Ihre Reise, Tag für Tag",
  dayN: "Tag {n}",
  yourTotal: "Ihr Gesamtpreis",
  earnsPoints: "Bringt {points} {programme}-Punkte",
  depositLine: "Anzahlung {deposit} bei Annahme · Restbetrag {balance} später",
  depositTerms: "Anzahlung bei Annahme, Restbetrag später",
  questions: "Fragen an Ihren Berater",
  questionsEmpty: "Noch etwas unklar? Fragen Sie hier, Ihr Berater antwortet auf dieser Seite.",
  answerSoon: "Ihr Berater antwortet in Kürze",
  askPlaceholder: "Schreiben Sie Ihre Frage…",
  ask: "Fragen",
  acceptPay: "Annehmen und Anzahlung leisten",
  preContractTitle: "Vor der Annahme: vorvertragliche Informationen",
  preContractLead: "Dies ist eine Pauschalreise. Das Gesetz verlangt, dass wir Ihnen diese Standardinformationen vor der Zahlung geben: wer Ihre Reise veranstaltet, Ihre wichtigsten Rechte und wer Sie bei Insolvenz absichert.",
  preContractOpen: "Vorvertragliche Informationen lesen",
  preContractCheck: "Ich habe die vorvertraglichen Informationen gelesen und akzeptiere sie.",
  preContractRequired: "Kreuzen Sie das Kästchen an, um zu bestätigen, dass Sie die vorvertraglichen Informationen gelesen haben.",
  preContractChanged: "Das Angebot hat sich seit dem Öffnen geändert. Lesen Sie die aktualisierten Informationen und kreuzen Sie erneut an.",
  preContractAccepted: "Vorvertragliche Informationen (Version {version}), angenommen am {date}",
  accepting: "Einen Moment…",
  decline: "Jetzt nicht / Ablehnen",
  declineConfirm: "Dieses Angebot ablehnen? Ihr Berater wird informiert.",
  declineReason: "Sagen Sie uns warum (optional)",
  confirmDecline: "Ja, ablehnen",
  proposalAccepted: "Sie haben dieses Angebot angenommen",
  proposalAcceptedBody: "Ihre Reise ist bereit; die Anzahlung wartet auf ihrer Seite.",
  proposalDeclined: "Dieses Angebot wurde abgelehnt",
  proposalDeclinedBody: "Falls Sie es sich anders überlegen, ist Ihr Berater nur eine Nachricht entfernt.",
  proposalExpired: "Dieses Angebot ist abgelaufen",
  proposalExpiredBody: "Bitten Sie Ihren Berater um ein aktualisiertes Angebot.",
  proposalClosed: "Dieses Angebot ist geschlossen.",
  proposalNotYours: "Dieses Angebot gehört einem anderen Kunden. Melden Sie sich mit der Adresse an, an die es gesendet wurde.",
  proposalSignIn: "Melden Sie sich an, um Ihr Angebot zu öffnen.",
  viewProposal: "Angebot ansehen",
  welcomeTrip: "Willkommen zu Ihrer Reise",
  welcomeTripBody: "Leisten Sie unten die Anzahlung, und wir beginnen mit den Buchungen.",
  payments: "Zahlungen",
  deposit: "Anzahlung",
  balance: "Restbetrag",
  instalmentN: "Rate {n}",
  refund: "Erstattung",
  balanceDueOn: "Restbetrag fällig am {date}",
  paidOf: "{paid} von {total} bezahlt",
  outstanding: "Offen",
  dueOn: "Fällig am {date}",
  payAmount: "{amount} zahlen",
  payNow: "Jetzt zahlen",
  paying: "Wird verarbeitet…",
  paymentMethod: "Zahlungsart",
  methodCard: "Karte",
  methodMbway: "MB Way",
  methodTransfer: "Überweisung",
  methodApplePay: "Apple Pay",
  methodGooglePay: "Google Pay",
  simulation: "Simulation (Testumgebung)",
  simulationBody: "Hier fließt kein Geld. Wählen Sie, wie die Zahlung ausgehen soll.",
  simApprove: "Zahlung genehmigen",
  simDecline: "Karte ablehnen",
  simPending: "Offen lassen (Überweisung)",
  payPaid: "Zahlung eingegangen",
  payPaidBody: "Vielen Dank. Ihre Quittung finden Sie in Ihren Nachrichten.",
  payFailed: "Die Zahlung ist fehlgeschlagen",
  payFailedBody: "Bitte versuchen Sie es erneut oder wählen Sie eine andere Zahlungsart.",
  payPending: "Zahlung ausstehend",
  payPendingBody: "Wir bestätigen sie, sobald die Überweisung eingeht.",
  errAlreadyPaid: "Diese Zahlung wurde bereits geleistet.",
  errBadState: "Diese Zahlung kann derzeit nicht ausgeführt werden.",
  errInstalments: "Ratenzahlung ist für diesen Restbetrag nicht möglich.",
  errHolderOnly: "Nur der Buchungsinhaber kann zahlen.",
  payMonthly: "Restbetrag in Raten zahlen",
  payMonthlyBody: "Teilen Sie den Restbetrag in Monatsraten auf, alle vor dem Fälligkeitsdatum.",
  monthsTimes: "{months} × {monthly}",
  firstLast: "Erste am {first} · letzte am {last}",
  instalmentsChosen: "Restbetrag in {n} Monatsraten",
  companionReadOnly: "Der Buchungsinhaber verwaltet die Zahlungen.",
  noPlanYet: "Die Zahlungsdetails erscheinen, sobald die Buchung eingerichtet ist.",
  statusDue: "Fällig",
  statusPaid: "Bezahlt",
  statusFailed: "Fehlgeschlagen",
  statusRefunded: "Erstattet",
  planDepositDue: "Anzahlung fällig",
  planDepositPaid: "Anzahlung bezahlt",
  planBalanceDue: "Restbetrag fällig",
  planPaid: "Vollständig bezahlt",
  planOverdue: "Überfällig",
  planRefunded: "Erstattet",
  travellerDetails: "Reisendendaten",
  travellersDueBy: "Reisendendaten bis {date}",
  travellerN: "Reisender {n}",
  trMissing: "Fehlt",
  trSubmitted: "Eingereicht",
  trValidated: "Geprüft",
  trRejected: "Zu korrigieren",
  enterDetails: "Daten eingeben",
  edit: "Bearbeiten",
  givenNames: "Vornamen",
  surname: "Nachname",
  dateOfBirth: "Geburtsdatum",
  nationality: "Staatsangehörigkeit",
  otherCountry: "Anderes Land (2-Buchstaben-Code)",
  countryCode: "Ländercode",
  documentType: "Dokument",
  passport: "Reisepass",
  idCard: "Personalausweis",
  documentNumber: "Dokumentnummer",
  documentExpiry: "Gültig bis",
  gender: "Geschlecht (optional)",
  genderF: "Weiblich",
  genderM: "Männlich",
  genderX: "Divers / keine Angabe",
  docsNote: "Dokumente werden nie als Bild gespeichert; nur diese Felder.",
  errAlreadyValidated: "Dieser Reisende wurde bereits geprüft; bitten Sie Ihren Berater um eine Änderung.",
  rejectedReason: "Grund: {reason}",
  travellerSaved: "Daten gespeichert. Ihr Berater prüft sie.",
  requests: "Änderungen und Stornierungen",
  requestsBody: "Sagen Sie Ihrem Berater, was Sie brauchen; die Referenz geht mit der Nachricht.",
  requestChange: "Änderung anfragen",
  requestCancellation: "Stornierung anfragen",
  changeSubject: "Änderungsanfrage · {ref}",
  cancelSubject: "Stornierungsanfrage · {ref}",
  payDeposit: "Anzahlung leisten",
  enterTravellers: "Reisendendaten eingeben",
  balanceDueHint: "Restbetrag fällig am {date}",
  // C3: conversation, assistant, requests
  navMessages: "Nachrichten",
  unread: "Neue Nachrichten",
  conversation: "Unterhaltung",
  conversationLead: "Ihre Nachrichten mit {name}, per E-Mail, WhatsApp und hier.",
  conversationEmpty: "Schreiben Sie {name}; der Assistent antwortet sofort, Ihr Berater übernimmt.",
  yourConsultantLower: "Ihrem Berater",
  assistant: "Assistent",
  channelHere: "Hier",
  kindChangeRequest: "Änderungsanfrage",
  kindCancellationRequest: "Stornierungsanfrage",
  kindQuestion: "Frage",
  kindHandoff: "An Ihren Berater übergeben",
  writePlaceholder: "Schreiben Sie Ihre Nachricht…",
  send: "Senden",
  replyMeOn: "Antwort per",
  replyHere: "Hier in der App",
  consultantTold: "Ihr Berater wurde informiert.",
  assistantNote: "Ich antworte sofort; Ihr Berater übernimmt, sobald Sie nach einer Person fragen.",
  assistantOpen: "Den Assistenten fragen",
  assistantEmpty: "Fragen Sie nach Ihren Dokumenten, dem Plan für morgen oder einem Detail Ihrer Reise.",
  assistantPlaceholder: "Fragen Sie etwas zu Ihrer Reise…",
  assistantThinking: "Einen Moment…",
  chipDocuments: "Wo sind meine Dokumente?",
  chipTomorrow: "Was ist morgen geplant?",
  chipPerson: "Mit einer Person sprechen",
  requestsNote: "Ihr Berater klärt die Bedingungen mit den Anbietern und meldet sich mit den genauen Kosten, bevor sich etwas ändert.",
  requestChangePlaceholder: "Was möchten Sie ändern? Daten, Reisende, Hotel, Flüge…",
  requestCancelPlaceholder: "Was möchten Sie stornieren, und ab wann?",
  requestSent: "Anfrage gesendet",
  requestSentBody: "Ihr Berater antwortet in Ihrer Unterhaltung.",
  openConversation: "Unterhaltung öffnen",
  newRequest: "Weitere Anfrage stellen",
  errHolderOnlyRequest: "Nur der Buchungsinhaber kann Änderungen oder Stornierungen anfragen.",
  replyChannel: "Antwortkanal",
  replyChannelBody: "Wohin die Antworten Ihres Beraters gehen, wenn Sie von hier schreiben.",
  writeToMe: "Mir schreiben",
  speaks: "Spricht: {langs}",
  // C4: trip companion
  scheduleChanged: "Flugzeiten geändert",
  wasTimes: "war",
  statusDriverAssigned: "Fahrer zugeteilt",
  statusEnRoute: "Unterwegs",
  driver: "Fahrer",
  plate: "Kennzeichen",
  callDriver: "Fahrer anrufen",
  followLive: "Wagen live verfolgen",
  driverOnTheWay: "Ihr Fahrer ist unterwegs",
  nextUp: "Als Nächstes",
  companions: "Mitreisende",
  companionsBody: "Mitreisende sehen den Plan und die Dokumente und erhalten die Hinweise; nur Sie zahlen und sprechen für die Buchung.",
  companionsReadOnly: "Die Buchungsinhaberin bzw. der Buchungsinhaber verwaltet die Mitreisenden.",
  invited: "Eingeladen",
  joined: "Beigetreten",
  inviteCompanion: "Mitreisende Person einladen",
  nameOptional: "Name (optional)",
  invite: "Einladen",
  inviting: "Wird eingeladen…",
  remove: "Entfernen",
  removeCompanionConfirm: "{name} aus dieser Reise entfernen?",
  errAlreadyMember: "Diese Person gehört bereits zur Reise.",
  errTooManyCompanions: "Diese Reise hat die Höchstzahl an Mitreisenden erreicht.",
  errHolderOnlyCompanions: "Nur die Buchungsinhaberin bzw. der Buchungsinhaber kann Mitreisende einladen.",
  tripNotices: "Reisehinweise",
  tripNoticesBody: "Wann wir Ihnen zu Ihren Reisen schreiben und wo wir es zuerst versuchen.",
  noticesBefore: "Vor der Reise",
  noticesBeforeHint: "Eine Übersicht eine Woche vorher und der Plan für morgen am Vortag.",
  noticesDuring: "Während der Reise",
  noticesDuringHint: "Der Plan jedes Morgens, Flugänderungen und Ihr Fahrer.",
  noticesAfter: "Nach der Reise",
  noticesAfterHint: "Wie es war und die gesammelten Punkte.",
  channelOrder: "Wo wir Sie zuerst erreichen",
  channelPush: "Benachrichtigung auf diesem Gerät",
  moveUp: "Nach oben",
  moveDown: "Nach unten",
  thisDevice: "Dieses Gerät",
  enablePush: "Benachrichtigungen auf diesem Gerät aktivieren",
  enablingPush: "Wird aktiviert…",
  pushEnabled: "Benachrichtigungen sind auf diesem Gerät aktiv.",
  pushDenied: "Benachrichtigungen sind für diese Seite in den Browsereinstellungen blockiert.",
  pushUnsupported: "Dieser Browser zeigt keine Benachrichtigungen. Fügen Sie die App dem Startbildschirm hinzu und versuchen Sie es von dort.",
  pushSimulated: "Push wird in dieser Umgebung simuliert: Die Hinweise sind in Ihren Nachrichten sichtbar.",
  pushFailed: "Benachrichtigungen konnten nicht aktiviert werden. Bitte erneut versuchen.",
  noticesBannerTitle: "Hinweise auf Ihrem Telefon erhalten",
  noticesBannerBody: "Installieren Sie die App und aktivieren Sie Benachrichtigungen: der Plan jedes Morgens und Live-Updates zu Flügen und Fahrern.",
  noticesBannerAction: "Benachrichtigungen aktivieren",
  // C5: Getaways Club und Sofort Hilfe
  navClub: "Club",
  clubEmptyTitle: "Ihr {programme}-Konto öffnet sich mit Ihrer ersten Reise",
  clubEmptyBody: "Jeder Euro bei einer unserer drei Marken bringt Punkte, und 100 Punkte sparen 1 Euro bei Ihrer nächsten Restzahlung.",
  yourTier: "Ihre Stufe",
  progressToNext: "{pct} % bis {tier}",
  topTier: "Sie sind auf der höchsten Stufe",
  worth: "Wert {value}",
  howItWorks: "So funktioniert es",
  howEarn: "1 Punkt pro Euro oder Dollar bei einer unserer drei Marken.",
  howRedeem: "100 Punkte = 1 Einheit Rabatt auf die Restzahlung.",
  howExpire: "Punkte verfallen 24 Monate nach Ihrer letzten Reise.",
  clubBrandsNote: "Ein Konto für Azores Getaways, Portugal Getaways und Yes Getaways.",
  tiersTitle: "Stufen",
  current: "Aktuell",
  fromPoints: "Ab {points} Punkten",
  fromFirstTrip: "Ab Ihrer ersten Reise",
  viewClub: "Ihre Vorteile ansehen",
  usePoints: "Ihre {programme}-Punkte einlösen",
  redeemBody: "Sie haben {points} Punkte im Wert von {value} für diese Restzahlung.",
  pointsToUse: "Einzulösende Punkte",
  applyPoints: "Punkte einlösen",
  applying: "Wird eingelöst…",
  pointsApplied: "{points} Punkte eingelöst (−{value})",
  fewerPoints: "Weniger Punkte",
  morePoints: "Mehr Punkte",
  errNoBalance: "Sie haben noch keine Punkte für diese Restzahlung.",
  errTooManyPoints: "Das ist mehr, als Sie hier einlösen können. Wählen Sie weniger, in Hunderterschritten.",
  errNothingDue: "Für diese Reise ist nichts mehr zu zahlen.",
  errNotLinked: "Ihr Treuekonto ist noch nicht verknüpft. Ihr Berater hilft Ihnen gern.",
  helpNowBody: "Sagen Sie uns, was los ist. Der Assistent antwortet sofort und unser Team wird alarmiert.",
  helpWhat: "Was ist passiert?",
  helpWhatPlaceholder: "Beschreiben Sie kurz, was passiert ist.",
  helpWhere: "Wo sind Sie? (optional)",
  helpWherePlaceholder: "Hotel, Flughafen, Straße…",
  helpCanCall: "Sie dürfen mich anrufen",
  helpPhone: "Telefonnummer",
  helpPhoneHint: "Wir rufen Sie unter dieser Nummer zurück.",
  helpUrgency: "Wie dringend ist es?",
  urgencyUrgent: "Dringend",
  urgencyUrgentHint: "Ich brauche sofort Hilfe: Flug verpasst, Unfall, kein Zimmer.",
  urgencySoon: "Bald",
  urgencySoonHint: "Heute, aber ich kann etwas warten.",
  urgencyInfo: "Nur eine Frage",
  urgencyInfoHint: "Keine Eile, ich brauche eine Information.",
  agentCalled: "{agent} wird jetzt angerufen",
  lineAlerted: "Unsere 24h-Hotline wurde alarmiert.",
  call24h: "24h-Hotline anrufen",
  viewInMessages: "In Nachrichten ansehen",
  previousCases: "Frühere Fälle",
  openedAgo: "Eröffnet {when}",
  statusOpen: "Offen",
  statusClosed: "Geschlossen",
  helpSent: "Wir haben Ihre Anfrage erhalten",
  // C6: launch (privacy notice, consents, data requests)
  privacy: "Datenschutz",
  privacyVersion: "Version {version} · Verantwortlicher: {controller}",
  privacyBody: "Ihre Daten und Ihre Entscheidungen. Ideastation ist der Verantwortliche für die drei Marken; ein Konto, eine Datenschutzerklärung.",
  readPrivacy: "Datenschutzerklärung lesen",
  consentTitle: "Eine Markenfamilie, ein Konto",
  consentController: "Ideastation, Lda. ist der Verantwortliche für Ihre Daten bei den drei Marken.",
  iUnderstand: "Verstanden",
  consentMarketingTitle: "Auch Neuigkeiten erhalten (optional)",
  serviceAgreement: "Servicevereinbarung",
  consentAccepted: "Akzeptiert {brand}",
  consentPending: "Noch nicht akzeptiert",
  yourData: "Ihre Daten",
  exportData: "Meine Daten exportieren",
  deleteAccount: "Mein Konto löschen",
  dataRequestsNote: "Der Export ist eine JSON-Datei mit allem, was dieser Bereich über Sie speichert. Das Löschen des Kontos entfernt Ihre persönlichen Daten; Ihre Reisen bleiben als Geschäftsunterlagen in unseren Aufzeichnungen, ohne Ihren Namen.",
  exporting: "Datei wird vorbereitet…",
  exported: "Datei gespeichert",
  deleteAccountExplain: "Ihre Reisen bleiben als Geschäftsunterlagen in unseren Aufzeichnungen, ohne Ihren Namen. Wir senden Ihnen per E-Mail einen Bestätigungslink, 48 Stunden gültig; das Konto wird erst gelöscht, wenn Sie ihn öffnen.",
  deleteAccountConfirm: "Bestätigungslink senden",
  deleteRequested: "Wir haben Ihnen eine Bestätigungs-E-Mail gesendet. Öffnen Sie den Link innerhalb von 48 Stunden, um das Konto zu löschen.",
  deleteConfirmTitle: "Ihr Konto löschen",
  deleteConfirmBody: "Damit werden Ihr Konto und Ihre persönlichen Daten endgültig gelöscht. Ihre Reisen bleiben als Geschäftsunterlagen in unseren Aufzeichnungen, ohne Ihren Namen.",
  deleteForGood: "Endgültig löschen",
  deleting: "Wird gelöscht…",
  keepAccount: "Mein Konto behalten",
  deleteDone: "Ihr Konto wurde gelöscht.",
  deleteDoneBody: "Danke, dass Sie mit uns gereist sind. Sie können diese Seite schließen.",
  deleteLinkExpired: "Dieser Link ist abgelaufen oder wurde bereits verwendet.",
};

export const CATALOGS: Record<Lang, Catalog> = { en, pt, es, fr, de };

export type Vars = Record<string, string | number>;

/** Translates a key in a language, filling `{name}` placeholders. */
export function translate(lang: Lang, key: Key, vars?: Vars): string {
  const text = CATALOGS[lang][key] ?? en[key];
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (match, name: string) => (name in vars ? String(vars[name]) : match));
}

export const LANG_STORAGE = "lang";

export function storedLang(): Lang | undefined {
  try {
    const v = localStorage.getItem(LANG_STORAGE);
    return isLang(v) ? v : undefined;
  } catch {
    return undefined;
  }
}

export function storeLang(lang: Lang) {
  try {
    localStorage.setItem(LANG_STORAGE, lang);
  } catch {
    // Private mode or storage blocked: the choice lasts for the session only.
  }
}

export function navigatorLang(): Lang {
  for (const l of navigator.languages ?? [navigator.language]) {
    const short = l.slice(0, 2).toLowerCase();
    if (isLang(short)) return short;
  }
  return "en";
}

/** The language to show: the stored choice, the identity's, else the browser's. */
export function resolveLang(identityLanguage?: string | null): Lang {
  return storedLang() ?? (isLang(identityLanguage) ? identityLanguage : undefined) ?? navigatorLang();
}

export const ERROR_KEYS: Record<string, Key> = {
  invalid_address: "errInvalidAddress",
  too_many_codes: "errTooManyCodes",
  unknown_phone: "errUnknownPhone",
  provider_unavailable: "errProviderUnavailable",
  wrong_code: "errWrongCode",
  challenge_expired: "errChallengeExpired",
  too_many_attempts: "errTooManyAttempts",
  no_brand: "errNoBrand",
  offline: "offline",
};

export const STATUS_KEYS: Record<string, Key> = {
  proposal: "statusProposal",
  pending: "statusPending",
  confirmed: "statusConfirmed",
  in_progress: "statusInProgress",
  completed: "statusCompleted",
  cancelled: "statusCancelled",
  due: "statusDue",
  paid: "statusPaid",
  failed: "statusFailed",
  refunded: "statusRefunded",
  changed: "scheduleChanged",
  driver_assigned: "statusDriverAssigned",
  en_route: "statusEnRoute",
  open: "statusOpen",
  closed: "statusClosed",
};

export const PLAN_STATUS_KEYS: Record<string, Key> = {
  deposit_due: "planDepositDue",
  deposit_paid: "planDepositPaid",
  balance_due: "planBalanceDue",
  paid: "planPaid",
  overdue: "planOverdue",
  refunded: "planRefunded",
};

export const TRAVELLER_STATUS_KEYS: Record<string, Key> = {
  missing: "trMissing",
  submitted: "trSubmitted",
  validated: "trValidated",
  rejected: "trRejected",
};

export const PAYMENT_ERROR_KEYS: Record<string, Key> = {
  already_paid: "errAlreadyPaid",
  bad_state: "errBadState",
  nothing_due: "errBadState",
  payment_not_found: "errBadState",
  instalments_not_possible: "errInstalments",
  holder_only: "errHolderOnly",
  already_validated: "errAlreadyValidated",
};

export const LOYALTY_ERROR_KEYS: Record<string, Key> = {
  no_balance: "errNoBalance",
  too_many_points: "errTooManyPoints",
  nothing_due: "errNothingDue",
  not_linked: "errNotLinked",
  holder_only: "errHolderOnly",
  crm_unavailable: "errProviderUnavailable",
};

export const URGENCY_KEYS: Record<string, Key> = {
  urgent: "urgencyUrgent",
  soon: "urgencySoon",
  info: "urgencyInfo",
};
